import { currentDivisionId } from 'common/src/stores/useFiltersStore';
import { getSettlementsByExporterId } from 'common/src/api/settlements';
import { Settlement } from 'common/src/utils/types';
import { useEffect, useState, useRef, useMemo } from 'react';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import {
  Select,
  SelectItem,
  Button,
  Card,
  CardBody,
  Checkbox,
  Divider,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Switch,
} from '@nextui-org/react';
import {
  ComparativeClientsUtility,
  GroupedSettlement,
  MainClientDetailWithComparable,
  VarietyStats,
} from '../../../services/comparative-clientes/comparative-clients.utility';
import { Icon } from '@iconify/react';
import { formatMoneyUSD } from '../../../utils/functions';
import { getWeekKey, sortWeekKeys } from 'common/src/utils/functions';
import ShipmentBarChart from '../../../components/specifics/settlements-client-comparision/ShipmentBarChart';
import VarietyComparisionBarChart from '../../../components/specifics/settlements-client-comparision/VarietyComparisionBarChart';
import CaliberComparisionBarChart from '../../../components/specifics/settlements-client-comparision/CaliberComparisionBarChart';
import RevenueBreakdownTable from '../../../components/specifics/settlements-client-comparision/RevenueBreakdownTable';
import { logUserAction } from '../../../services/actionsService';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import MarketFilter from '../../../../../common/src/components/filters/MarketFilter';
import InformationTooltip from '../../../../../common/src/components/ui/InformationTooltip';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import { createClaim, createClaimDetails } from 'common/src/api/claims';
import { toast } from 'react-toastify';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import jsPDF from 'jspdf';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageLayout,
} from '../../../components/ui';
import autoTable from 'jspdf-autotable';
import { getBrandAssets } from 'common/src/utils/branding';

interface SummaryStatistics {
  totalFobDifference: number;
  perKiloDifference: number;
  varietyStats: VarietyStats[];
  mainClient: {
    totalFob: number;
    totalKilos: number;
    avgFobPerKilo: number;
  };
  comparison: {
    totalFob: number;
    totalKilos: number;
    avgFobPerKilo: number;
  };
  comparablePercentage: number;
}

interface DetailType {
  kilos: number;
  fob: number;
  fobPerKg: number;
}

interface ComparisonClientData {
  totalKilos: number;
  totalFob: number;
  details: Record<string, DetailType>;
  dates: string[];
}

interface SettlementWithSelection extends Settlement {
  _isMainSelection?: boolean;
  _isComparisonSelection?: boolean;
}

// Define an interface for the differences to fix TypeScript issues
interface ClaimDifference {
  settlement_detail_id: number;
  productKey: string; // build it next to variety/caliber in your comparison
  kilos: number; // already available
  mainFobPerKg: number;
  comparisonFobPerKg: number;
  difference: number; // negative ⇒ client owes
}

interface PreparedClaimData {
  mainClient: Settlement | null;
  differences: ClaimDifference[];
  mainClientDetails: MainClientDetailWithComparable[];
}

function buildClaimDifferences(grouped: {
  [k: string]: GroupedSettlement;
}): ClaimDifference[] {
  const diffs: ClaimDifference[] = [];

  Object.values(grouped).forEach((group) => {
    // recorre cada producto del cliente principal
    Object.entries(group.mainClient.details).forEach(([productKey, main]) => {
      // FOB-kg del principal
      const mainFobKg = main.fobPerKg;

      // promedia FOB-kg de todos los clientes de comparación que tengan ese producto
      let compKilos = 0;
      let compFob = 0;

      Object.values(group.comparisons).forEach((c) => {
        const d = c.details[productKey];
        if (d) {
          compKilos += d.kilos;
          compFob += d.fob;
        }
      });

      if (compKilos > 0) {
        const compFobKg = compFob / compKilos;
        const diff = mainFobKg - compFobKg; //  negativo ⇒ debe

        // 🔸 busca cada settlement_detail que compone ese productKey
        group.mainClient.dates.forEach((_) => {
          /* nada, sólo ilustrativo */
        });

        // -- En este ejemplo añadimos UNO genérico por producto.
        //    Si quieres el detalle exacto por settlement_detail
        //    itera sobre `selectedSettlements` y filtra por productKey.
        diffs.push({
          settlement_detail_id: 0, // ⇐ pon el id real aquí
          productKey,
          kilos: main.kilos,
          mainFobPerKg: Number(mainFobKg.toFixed(4)),
          comparisonFobPerKg: Number(compFobKg.toFixed(4)),
          difference: Number(diff.toFixed(4)),
        });
      }
    });
  });

  return diffs;
}

function buildProductDistribution(
  diffs: ClaimDifference[],
  totalClaimUsd: number,
  definitionString: string // the formula user picked
) {
  const byProd: Record<string, { owed: number; lines: ClaimDifference[] }> = {};
  diffs.forEach((d) => {
    const key = `${d.productKey}`; // you already build this key
    if (!byProd[key]) byProd[key] = { owed: 0, lines: [] };
    byProd[key].owed += Math.abs(d.difference); // negative → abs
    byProd[key].lines.push(d);
  });

  const grand = Object.values(byProd).reduce((acc, p) => acc + p.owed, 0);

  return Object.entries(byProd).map(([product_key, p]) => {
    const productShare = (p.owed / grand) * totalClaimUsd;
    return {
      product_key,
      product_definition: definitionString,
      amount_claim_usd: productShare,
      lines: p.lines.map((l) => ({
        settlement_detail_id: l.settlement_detail_id,
        portion_usd: (Math.abs(l.difference) / p.owed) * productShare,
      })),
      snapshot: {
        kilos: p.lines.reduce((k, l) => k + l.kilos, 0), // add whatever you want
        mainFobPerKg: p.lines[0].mainFobPerKg, // example
        comparisonFobPerKg: p.lines[0].comparisonFobPerKg,
        deltaUsd: -p.owed,
      },
    };
  });
}

const SettlementsComparativeClients = () => {
  const { t } = useTranslation('settlementsComparative');
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [allReceiverSettlements, setAllReceiverSettlements] = useState<
    Settlement[]
  >([]);
  const [isLoadingSettlements, setIsLoadingSettlements] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [dateGroupingType, setDateGroupingType] = useState<
    'date_sold' | 'ship_eta' | 'ship_etd'
  >('date_sold');
  const [comparisonClients, setComparisonClients] = useState<string[]>([]);
  const [selectedMainSettlements, setSelectedMainSettlements] = useState<
    string[]
  >(['all']);
  const [selectedComparisonSettlements, setSelectedComparisonSettlements] =
    useState<string[]>(['all']);
  const [isFormatEnabled, setIsFormatEnabled] = useState<boolean>(true);
  const [isLabelEnabled, setIsLabelEnabled] = useState<boolean>(false);
  const [isColorEnabled, setIsColorEnabled] = useState<boolean>(true);
  const [summaryStatistics, setSummaryStatistics] =
    useState<SummaryStatistics | null>(null);
  const [comparisonType, setComparisonType] = useState<
    'byWeeks' | 'byShips' | 'custom'
  >('byShips');
  const [priceType, setPriceType] = useState<'FOB' | 'CIF'>('FOB');
  const [shipmentData, setShipmentData] = useState<{
    byShips: { [key: string]: GroupedSettlement };
    byWeeks: { [key: string]: GroupedSettlement };
    custom?: { [key: string]: GroupedSettlement };
  }>({
    byShips: {},
    byWeeks: {},
    custom: {},
  });
  const [mainClientDetails, setMainClientDetails] = useState<
    MainClientDetailWithComparable[]
  >([]);
  const [sinisterFilter, setSinisterFilter] = useState<
    'all' | 'sinister' | 'non_sinister'
  >('all');
  const [sinisterFilterComparison, setSinisterFilterComparison] = useState<
    'all' | 'sinister' | 'non_sinister'
  >('all');
  const [shipmentTypeFilter, setShipmentTypeFilter] = useState<
    'A' | 'M' | 'T' | 'both'
  >('both');
  const [marketFilter, setMarketFilter] = useState<string[]>([]);
  const [isSavingClaim, setIsSavingClaim] = useState(false);
  const [claimDescription, setClaimDescription] = useState('');
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [preparedClaimData, setPreparedClaimData] = useState<PreparedClaimData>(
    {
      mainClient: null,
      differences: [],
      mainClientDetails: [],
    }
  );
  const resultsRef = useRef<HTMLDivElement>(null);
  const actionsCardRef = useRef<HTMLDivElement>(null);
  const [isOptionsChanged, setIsOptionsChanged] = useState(false);
  const [useLocalCurrency, setUseLocalCurrency] = useState(false);
  const [selectedLocalCurrency, setSelectedLocalCurrency] =
    useState<string>('');
  const [includeRevenuesMain, setIncludeRevenuesMain] = useState(false);
  const [includeRevenuesComparison, setIncludeRevenuesComparison] =
    useState(false);
  const [includeMarketInAir, setIncludeMarketInAir] = useState(false);

  // Apply market filter client-side (no refetch)
  const filteredSettlements = useMemo(() => {
    if (marketFilter.length === 0) return settlements;
    return settlements.filter((s) =>
      marketFilter.includes(s.market?.name || 'Sin Mercado')
    );
  }, [settlements, marketFilter]);

  const mainClientCurrencies = [
    ...new Set(
      filteredSettlements
        .filter((s) => s.receiver === selectedClient && s.local_currency)
        .map((s) => s.local_currency)
    ),
  ];

  // Auto-select currency when there's only one
  const effectiveLocalCurrency = useLocalCurrency
    ? mainClientCurrencies.length === 1
      ? mainClientCurrencies[0]
      : selectedLocalCurrency
    : '';

  // Currency-aware formatter
  const formatMoney = (
    value: number,
    opts: { decimals?: number } = {}
  ): string => {
    const decimals = opts.decimals ?? 2;
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    if (useLocalCurrency && effectiveLocalCurrency) {
      return `${formatted} ${effectiveLocalCurrency}`;
    }
    return formatMoneyUSD(value, opts);
  };

  const currencyLabel =
    useLocalCurrency && effectiveLocalCurrency ? effectiveLocalCurrency : 'USD';

  const [diffContextComparableOnly, setDiffContextComparableOnly] =
    useState(false);

  const diffContext = useMemo(() => {
    if (!summaryStatistics || !shipmentData) return null;
    const activeData =
      shipmentData[comparisonType === 'custom' ? 'byShips' : comparisonType];
    if (!activeData || Object.keys(activeData).length === 0) return null;

    const diff = summaryStatistics.totalFobDifference;

    // Build set of comparable detail IDs for filtering
    const comparableDetailIds = diffContextComparableOnly
      ? new Set(
          mainClientDetails
            .filter((d) => d.hasComparison)
            .map((d) => d.settlement_detail_id)
        )
      : null;

    // Compute FOB (always actual FOB, never CIF) and commission from raw settlements
    const receiverSettlements = allReceiverSettlements.filter(
      (s) => s.receiver === selectedClient
    );
    let totalFob = 0,
      totalCommission = 0;
    receiverSettlements.forEach((s) => {
      s.settlement_details?.forEach((d) => {
        // In comparable mode, skip details without a match
        if (comparableDetailIds && !comparableDetailIds.has(d.id)) return;

        const exchangeRate = d.exchange_rate || 1;
        // FOB is always FOB (not CIF), regardless of priceType
        if (useLocalCurrency) {
          const netLocal =
            (d.local_total_price || 0) -
            (d.local_total_tax || 0) -
            (d.local_total_commision || 0) -
            (d.local_other_costs || 0) -
            (d.local_repacking_costs || 0);
          const freightLocal = (d.usd_freight || 0) * exchangeRate;
          totalFob += netLocal - freightLocal;
          totalCommission += d.local_total_commision || 0;
        } else {
          totalFob += d.usd_total_fob || 0;
          totalCommission += (d.local_total_commision || 0) / exchangeRate;
        }
      });
    });

    return {
      pctOfFob: totalFob !== 0 ? (diff / totalFob) * 100 : null,
      pctOfCommission:
        totalCommission !== 0 ? (diff / totalCommission) * 100 : null,
    };
  }, [
    summaryStatistics,
    shipmentData,
    comparisonType,
    diffContextComparableOnly,
    allReceiverSettlements,
    selectedClient,
    useLocalCurrency,
    mainClientDetails,
  ]);

  const exportToExcel = (withDetails = false) => {
    ComparativeClientsUtility.exportToExcel(
      shipmentData[comparisonType === 'custom' ? 'byShips' : comparisonType],
      selectedClient,
      isFormatEnabled,
      isLabelEnabled,
      isColorEnabled,
      priceType,
      mainClientDetails,
      withDetails,
      currencyLabel
    );
  };

  const loadImageAsBase64 = (src: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  const formatShipLabel = (key: string): string => {
    if (key.startsWith('W')) return `Week ${key.slice(1)}`;
    const parts = key.split('-');
    return parts.length > 1 ? parts.slice(1).join(' ').replace(/_/g, ' ') : key;
  };

  const exportToPDF = async () => {
    if (!shipmentData || !summaryStatistics) return;

    const activeData =
      shipmentData[comparisonType === 'custom' ? 'byShips' : comparisonType];
    if (!activeData || Object.keys(activeData).length === 0) return;

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 18;
      const contentWidth = pageWidth - margin * 2;
      const centerX = pageWidth / 2;

      const cherry = { r: 190, g: 18, b: 60 };
      const charcoal = { r: 24, g: 24, b: 27 };
      const slate = { r: 71, g: 85, b: 105 };
      const muted = { r: 148, g: 163, b: 184 };
      const faintBg = { r: 248, g: 250, b: 252 };
      const borderC = { r: 226, g: 232, b: 240 };
      const green = { r: 22, g: 163, b: 74 };
      const red = { r: 220, g: 38, b: 38 };
      const logoAspect = 1575 / 245;

      const whiteLogoBase64 = await loadImageAsBase64(
        getBrandAssets().fullLogoDark
      );

      const today = new Date();
      const dateStr = today.toLocaleDateString('en-US', {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      });

      const compClientsLabel = comparisonClients.join(', ');

      // Footer helper
      const drawFooter = () => {
        const totalPages = doc.getNumberOfPages();
        for (let p = 1; p <= totalPages; p++) {
          doc.setPage(p);
          const footH = 10;
          doc.setFillColor(charcoal.r, charcoal.g, charcoal.b);
          doc.rect(0, pageHeight - footH, pageWidth, footH, 'F');
          doc.setFillColor(cherry.r, cherry.g, cherry.b);
          doc.rect(0, pageHeight - footH, pageWidth, 1, 'F');
          if (whiteLogoBase64) {
            const fH = 3.5;
            doc.addImage(
              whiteLogoBase64,
              'PNG',
              margin,
              pageHeight - footH / 2 - fH / 2,
              fH * logoAspect,
              fH
            );
          }
          doc.setFontSize(6);
          doc.setTextColor(160, 160, 160);
          doc.setFont('helvetica', 'normal');
          doc.text(
            `Confidential  ·  ${dateStr}  ·  Page ${p}/${totalPages}`,
            pageWidth - margin,
            pageHeight - footH / 2 + 1.5,
            { align: 'right' }
          );
        }
      };

      // ═══ HEADER ═══
      const headerH = 26;
      doc.setFillColor(charcoal.r, charcoal.g, charcoal.b);
      doc.rect(0, 0, pageWidth, headerH, 'F');
      doc.setFillColor(cherry.r, cherry.g, cherry.b);
      doc.rect(0, headerH, pageWidth, 1.2, 'F');
      if (whiteLogoBase64) {
        const lH = 6.5;
        doc.addImage(
          whiteLogoBase64,
          'PNG',
          margin,
          (headerH - lH) / 2,
          lH * logoAspect,
          lH
        );
      }
      const certNum = `N° ${Date.now().toString().slice(-8)}`;
      doc.setFontSize(7);
      doc.setTextColor(130, 130, 130);
      doc.setFont('helvetica', 'normal');
      doc.text(certNum, pageWidth - margin, headerH / 2 + 1, {
        align: 'right',
      });

      // ═══ TITLE ═══
      let y = headerH + 13;
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(charcoal.r, charcoal.g, charcoal.b);
      doc.text('C E R T I F I C A T E', centerX, y, { align: 'center' });
      y += 8;
      doc.setFontSize(10.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(cherry.r, cherry.g, cherry.b);
      doc.text('Client Comparison Report', centerX, y, { align: 'center' });

      // Double rule
      y += 5;
      const ruleL = margin + 35;
      const ruleR = pageWidth - margin - 35;
      doc.setDrawColor(borderC.r, borderC.g, borderC.b);
      doc.setLineWidth(0.35);
      doc.line(ruleL, y, ruleR, y);
      doc.setLineWidth(0.12);
      doc.line(ruleL, y + 1.4, ruleR, y + 1.4);

      // ═══ INFO PANEL ═══
      y += 7;
      const panelH = 27;
      doc.setFillColor(faintBg.r, faintBg.g, faintBg.b);
      doc.setDrawColor(borderC.r, borderC.g, borderC.b);
      doc.setLineWidth(0.2);
      doc.roundedRect(margin, y, contentWidth, panelH, 2.5, 2.5, 'FD');

      const sinisterLabel =
        sinisterFilter === 'all'
          ? 'All'
          : sinisterFilter === 'sinister'
            ? 'Claims Only'
            : 'No Claims';
      const sinisterLabelComparison =
        sinisterFilterComparison === 'all'
          ? 'All'
          : sinisterFilterComparison === 'sinister'
            ? 'Claims Only'
            : 'No Claims';
      const groupLabel =
        comparisonType === 'byShips'
          ? 'By Ship'
          : comparisonType === 'byWeeks'
            ? 'By Week'
            : 'Custom';

      const infoItems = [
        [
          { label: 'EXPORTER', value: userProfile?.exporter?.name || '-' },
          { label: 'MAIN CLIENT', value: selectedClient },
          { label: 'SEASON', value: selectedSeason || '-' },
        ],
        [
          { label: 'INCOTERM', value: priceType },
          { label: 'CURRENCY', value: currencyLabel },
        ],
      ];

      const pad = 6;
      const cols = [
        margin + pad,
        margin + contentWidth * 0.35,
        margin + contentWidth * 0.67,
      ];
      const rowYs = [y + pad, y + pad + 12];

      infoItems.forEach((row, ri) => {
        row.forEach((item, ci) => {
          doc.setFontSize(5.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(muted.r, muted.g, muted.b);
          doc.text(item.label, cols[ci], rowYs[ri]);
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(slate.r, slate.g, slate.b);
          // Truncate long values
          const maxW = contentWidth * 0.3;
          const val =
            doc.getTextWidth(item.value) > maxW
              ? item.value.substring(0, 30) + '...'
              : item.value;
          doc.text(val, cols[ci], rowYs[ri] + 4.2);
        });
      });

      // ═══ KPI CARDS ═══
      y += panelH + 8;
      const kpiGap = 4;
      const kpiW = (contentWidth - kpiGap * 2) / 3;
      const kpiH = 24;

      const totalDiff = summaryStatistics.totalFobDifference;
      const perKgDiff = summaryStatistics.perKiloDifference;
      const compPct = summaryStatistics.comparablePercentage ?? 0;

      const kpis = [
        {
          label: `TOTAL ${currencyLabel} DIFFERENCE`,
          value: formatMoney(totalDiff, { decimals: 0 }),
          accent: totalDiff >= 0 ? green : red,
        },
        {
          label: `${currencyLabel} / KG DIFFERENCE`,
          value: formatMoney(perKgDiff, { decimals: 2 }),
          accent: perKgDiff >= 0 ? green : red,
        },
        {
          label: 'COMPARABLE PERCENTAGE',
          value: `${compPct}%`,
          accent: charcoal,
        },
      ];

      kpis.forEach((kpi, i) => {
        const kx = margin + i * (kpiW + kpiGap);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(borderC.r, borderC.g, borderC.b);
        doc.setLineWidth(0.2);
        doc.roundedRect(kx, y, kpiW, kpiH, 2, 2, 'FD');
        doc.setFillColor(kpi.accent.r, kpi.accent.g, kpi.accent.b);
        doc.roundedRect(kx, y, 2.2, kpiH, 2, 0, 'F');
        doc.rect(kx + 1.2, y, 1.2, kpiH, 'F');
        doc.setFontSize(5.2);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(muted.r, muted.g, muted.b);
        doc.text(kpi.label, kx + 7, y + 7);
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(kpi.accent.r, kpi.accent.g, kpi.accent.b);
        doc.text(kpi.value, kx + 7, y + 17.5);
      });

      // ═══ BREAKDOWN HELPERS ═══
      y += kpiH + 10;

      const sectionTitle = (label: string, yPos: number) => {
        doc.setFillColor(cherry.r, cherry.g, cherry.b);
        doc.rect(margin, yPos - 3, 2.5, 2.5, 'F');
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(charcoal.r, charcoal.g, charcoal.b);
        doc.text(label, margin + 5.5, yPos);
      };

      const colorDiffCell = (data: any, colIdx: number) => {
        if (data.section === 'body' && data.column.index === colIdx) {
          const raw = data.cell.raw as string;
          if (raw && raw !== '-') {
            data.cell.styles.textColor = raw.includes('-')
              ? [red.r, red.g, red.b]
              : [green.r, green.g, green.b];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      };

      const miniStyles = {
        styles: {
          fontSize: 7,
          cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
          lineColor: [borderC.r, borderC.g, borderC.b],
          lineWidth: 0.12,
          textColor: [slate.r, slate.g, slate.b],
        },
        headStyles: {
          fillColor: [charcoal.r, charcoal.g, charcoal.b],
          textColor: [255, 255, 255],
          fontStyle: 'bold' as const,
          fontSize: 6.5,
          cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
        },
        alternateRowStyles: { fillColor: [faintBg.r, faintBg.g, faintBg.b] },
      };

      const miniColStyles7 = {
        1: { halign: 'right' as const },
        2: { halign: 'right' as const },
        3: { halign: 'right' as const },
        4: { halign: 'right' as const },
        5: { halign: 'right' as const },
        6: { halign: 'right' as const },
      };

      const miniColStyles5 = {
        1: { halign: 'right' as const },
        2: { halign: 'right' as const },
        3: { halign: 'right' as const },
        4: { halign: 'right' as const },
      };

      // ═══ 1. BREAKDOWN BY REVENUE / COSTS ═══
      {
        let rcMainRevenue = 0,
          rcMainCommission = 0,
          rcMainTax = 0,
          rcMainOtherCosts = 0,
          rcMainFreight = 0,
          rcMainKilos = 0;
        // Comparable weighted by main kilos (like-for-like)
        let rcCompRevenueW = 0,
          rcCompCommissionW = 0,
          rcCompTaxW = 0,
          rcCompOtherCostsW = 0,
          rcCompFreightW = 0;
        let rcRevenueDiff = 0,
          rcCommissionDiff = 0,
          rcTaxDiff = 0,
          rcOtherCostsDiff = 0,
          rcFreightDiff = 0;

        Object.values(activeData).forEach((group) => {
          Object.entries(group.mainClient.details).forEach(
            ([detailKey, mainDetail]) => {
              let hasCmp = false;
              let dCR = 0,
                dCC = 0,
                dCT = 0,
                dCO = 0,
                dCFr = 0,
                dCK = 0;
              Object.values(group.comparisons).forEach((client) => {
                const cd = client.details[detailKey];
                if (cd && cd.kilos > 0) {
                  hasCmp = true;
                  dCR += cd.totalRevenue;
                  dCC += cd.totalCommission;
                  dCT += cd.totalTax;
                  dCO += cd.totalOtherCosts;
                  dCFr += cd.totalFreight;
                  dCK += cd.kilos;
                }
              });
              if (hasCmp && mainDetail.kilos > 0) {
                const mk = mainDetail.kilos,
                  ck = dCK;
                rcMainRevenue += mainDetail.totalRevenue;
                rcMainCommission += mainDetail.totalCommission;
                rcMainTax += mainDetail.totalTax;
                rcMainOtherCosts += mainDetail.totalOtherCosts;
                rcMainFreight += mainDetail.totalFreight;
                rcMainKilos += mk;
                // Comparable per-kg weighted by main kilos
                rcCompRevenueW += (dCR / ck) * mk;
                rcCompCommissionW += (dCC / ck) * mk;
                rcCompTaxW += (dCT / ck) * mk;
                rcCompOtherCostsW += (dCO / ck) * mk;
                rcCompFreightW += (dCFr / ck) * mk;
                rcRevenueDiff += (mainDetail.totalRevenue / mk - dCR / ck) * mk;
                rcCommissionDiff +=
                  (mainDetail.totalCommission / mk - dCC / ck) * mk;
                rcTaxDiff += (mainDetail.totalTax / mk - dCT / ck) * mk;
                rcOtherCostsDiff +=
                  (mainDetail.totalOtherCosts / mk - dCO / ck) * mk;
                rcFreightDiff +=
                  (mainDetail.totalFreight / mk - dCFr / ck) * mk;
              }
            }
          );
        });

        if (rcMainKilos > 0) {
          const rcNetDiff =
            rcRevenueDiff -
            rcCommissionDiff -
            rcTaxDiff -
            rcOtherCostsDiff -
            rcFreightDiff;
          const rcMainNetPerKg =
            (rcMainRevenue -
              rcMainCommission -
              rcMainTax -
              rcMainOtherCosts -
              rcMainFreight) /
            rcMainKilos;
          const rcCompNetPerKg =
            (rcCompRevenueW -
              rcCompCommissionW -
              rcCompTaxW -
              rcCompOtherCostsW -
              rcCompFreightW) /
            rcMainKilos;

          const rcRow = (
            label: string,
            mainPerKg: number,
            compPerKg: number,
            diffTotal: number
          ) => [
            label,
            formatMoney(mainPerKg, { decimals: 2 }),
            formatMoney(compPerKg, { decimals: 2 }),
            formatMoney(diffTotal / rcMainKilos, { decimals: 2 }),
            formatMoney(diffTotal, { decimals: 0 }),
          ];

          const rcRows = [
            rcRow(
              'Revenue',
              rcMainRevenue / rcMainKilos,
              rcCompRevenueW / rcMainKilos,
              rcRevenueDiff
            ),
            rcRow(
              'Commission',
              -(rcMainCommission / rcMainKilos),
              -(rcCompCommissionW / rcMainKilos),
              -rcCommissionDiff
            ),
            rcRow(
              'Taxes',
              -(rcMainTax / rcMainKilos),
              -(rcCompTaxW / rcMainKilos),
              -rcTaxDiff
            ),
            rcRow(
              'Other Costs',
              -(rcMainOtherCosts / rcMainKilos),
              -(rcCompOtherCostsW / rcMainKilos),
              -rcOtherCostsDiff
            ),
            ...(priceType === 'FOB'
              ? [
                  rcRow(
                    'Freight',
                    -(rcMainFreight / rcMainKilos),
                    -(rcCompFreightW / rcMainKilos),
                    -rcFreightDiff
                  ),
                ]
              : []),
          ];

          sectionTitle('Breakdown by Revenue / Costs', y);
          y += 4;

          autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin, bottom: 18 },
            head: [
              [
                'Component',
                `Own ${currencyLabel}/KG`,
                `Comp. ${currencyLabel}/KG`,
                `Diff. ${currencyLabel}/KG`,
                'Total Diff.',
              ],
            ],
            body: rcRows,
            foot: [
              [
                'NET',
                formatMoney(rcMainNetPerKg, { decimals: 2 }),
                formatMoney(rcCompNetPerKg, { decimals: 2 }),
                formatMoney(rcNetDiff / rcMainKilos, { decimals: 2 }),
                formatMoney(rcNetDiff, { decimals: 0 }),
              ],
            ],
            ...miniStyles,
            footStyles: {
              fillColor: [charcoal.r, charcoal.g, charcoal.b],
              textColor: [255, 255, 255],
              fontStyle: 'bold' as const,
              fontSize: 7,
              cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
            },
            columnStyles: {
              1: { halign: 'right' as const },
              2: { halign: 'right' as const },
              3: { halign: 'right' as const },
              4: { halign: 'right' as const },
            },
            didParseCell: (data: any) => {
              colorDiffCell(data, 3);
              colorDiffCell(data, 4);
            },
          });
        }
      }

      // ═══ 2. BREAKDOWN BY VARIETY ═══
      const varBd: Record<
        string,
        { kilos: number; othersKilos: number; diff: number }
      > = {};
      Object.values(activeData).forEach((group) => {
        Object.entries(group.mainClient.details).forEach(([dk, md]) => {
          const v = dk.split('-')[0];
          let cK = 0,
            cF = 0;
          Object.values(group.comparisons).forEach((c) => {
            const d = c.details[dk];
            if (d) {
              cK += d.kilos;
              cF += d.fob;
            }
          });
          if (md.kilos > 0 && cK > 0) {
            if (!varBd[v]) varBd[v] = { kilos: 0, othersKilos: 0, diff: 0 };
            varBd[v].kilos += md.kilos;
            varBd[v].othersKilos += cK;
            varBd[v].diff += (md.fobPerKg - cF / cK) * md.kilos;
          }
        });
      });

      let bY = (doc as any).lastAutoTable?.finalY + 12 || y;
      sectionTitle('Breakdown by Variety', bY);
      bY += 4;

      autoTable(doc, {
        startY: bY,
        margin: { left: margin, right: margin, bottom: 18 },
        head: [
          [
            'Variety',
            'Own KG',
            'Comp. KG',
            'Total Diff.',
            `Diff. ${currencyLabel}/KG`,
          ],
        ],
        body: Object.entries(varBd)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([n, d]) => [
            n,
            Math.round(d.kilos).toLocaleString('es-CL'),
            Math.round(d.othersKilos).toLocaleString('es-CL'),
            formatMoney(d.diff, { decimals: 0 }),
            d.kilos > 0 ? formatMoney(d.diff / d.kilos, { decimals: 2 }) : '-',
          ]),
        foot: (() => {
          const tK = Object.values(varBd).reduce((a, d) => a + d.kilos, 0);
          const tOK = Object.values(varBd).reduce(
            (a, d) => a + d.othersKilos,
            0
          );
          const tD = Object.values(varBd).reduce((a, d) => a + d.diff, 0);
          return [
            [
              'TOTAL',
              Math.round(tK).toLocaleString('es-CL'),
              Math.round(tOK).toLocaleString('es-CL'),
              formatMoney(tD, { decimals: 0 }),
              tK > 0 ? formatMoney(tD / tK, { decimals: 2 }) : '-',
            ],
          ];
        })(),
        ...miniStyles,
        footStyles: {
          fillColor: [charcoal.r, charcoal.g, charcoal.b],
          textColor: [255, 255, 255],
          fontStyle: 'bold' as const,
          fontSize: 7,
          cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
        },
        columnStyles: miniColStyles5,
        didParseCell: (data: any) => {
          colorDiffCell(data, 3);
          colorDiffCell(data, 4);
        },
      });

      // ═══ 3. BREAKDOWN BY SHIPMENT ═══
      const shipBd: Record<
        string,
        { kilos: number; othersKilos: number; diff: number }
      > = {};
      Object.entries(activeData).forEach(([key, group]) => {
        const label = formatShipLabel(key);
        Object.entries(group.mainClient.details).forEach(([dk, md]) => {
          let cK = 0,
            cF = 0;
          Object.values(group.comparisons).forEach((c) => {
            const d = c.details[dk];
            if (d) {
              cK += d.kilos;
              cF += d.fob;
            }
          });
          if (md.kilos > 0 && cK > 0) {
            if (!shipBd[label])
              shipBd[label] = { kilos: 0, othersKilos: 0, diff: 0 };
            shipBd[label].kilos += md.kilos;
            shipBd[label].othersKilos += cK;
            shipBd[label].diff += (md.fobPerKg - cF / cK) * md.kilos;
          }
        });
      });

      bY = (doc as any).lastAutoTable?.finalY + 12 || 200;
      sectionTitle('Breakdown by Shipment', bY);
      bY += 4;

      autoTable(doc, {
        startY: bY,
        margin: { left: margin, right: margin, bottom: 18 },
        head: [
          [
            'Shipment',
            'Own KG',
            'Comp. KG',
            'Total Diff.',
            `Diff. ${currencyLabel}/KG`,
          ],
        ],
        body: Object.entries(shipBd).map(([n, d]) => [
          n,
          Math.round(d.kilos).toLocaleString('es-CL'),
          Math.round(d.othersKilos).toLocaleString('es-CL'),
          formatMoney(d.diff, { decimals: 0 }),
          d.kilos > 0 ? formatMoney(d.diff / d.kilos, { decimals: 2 }) : '-',
        ]),
        foot: (() => {
          const tK = Object.values(shipBd).reduce((a, d) => a + d.kilos, 0);
          const tOK = Object.values(shipBd).reduce(
            (a, d) => a + d.othersKilos,
            0
          );
          const tD = Object.values(shipBd).reduce((a, d) => a + d.diff, 0);
          return [
            [
              'TOTAL',
              Math.round(tK).toLocaleString('es-CL'),
              Math.round(tOK).toLocaleString('es-CL'),
              formatMoney(tD, { decimals: 0 }),
              tK > 0 ? formatMoney(tD / tK, { decimals: 2 }) : '-',
            ],
          ];
        })(),
        ...miniStyles,
        footStyles: {
          fillColor: [charcoal.r, charcoal.g, charcoal.b],
          textColor: [255, 255, 255],
          fontStyle: 'bold' as const,
          fontSize: 7,
          cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
        },
        columnStyles: miniColStyles5,
        didParseCell: (data: any) => {
          colorDiffCell(data, 3);
          colorDiffCell(data, 4);
        },
      });

      // ═══ 4. BREAKDOWN BY SIZE ═══
      const calOrder = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
      const calBd: Record<
        string,
        { kilos: number; othersKilos: number; diff: number }
      > = {};
      Object.values(activeData).forEach((group) => {
        Object.entries(group.mainClient.details).forEach(([dk, md]) => {
          const c = dk.split('-')[1] || dk;
          let cK = 0,
            cF = 0;
          Object.values(group.comparisons).forEach((cl) => {
            const d = cl.details[dk];
            if (d) {
              cK += d.kilos;
              cF += d.fob;
            }
          });
          if (md.kilos > 0 && cK > 0) {
            if (!calBd[c]) calBd[c] = { kilos: 0, othersKilos: 0, diff: 0 };
            calBd[c].kilos += md.kilos;
            calBd[c].othersKilos += cK;
            calBd[c].diff += (md.fobPerKg - cF / cK) * md.kilos;
          }
        });
      });

      bY = (doc as any).lastAutoTable?.finalY + 12 || 200;
      sectionTitle('Breakdown by Size', bY);
      bY += 4;

      autoTable(doc, {
        startY: bY,
        margin: { left: margin, right: margin, bottom: 18 },
        head: [
          [
            'Size',
            'Own KG',
            'Comp. KG',
            'Total Diff.',
            `Diff. ${currencyLabel}/KG`,
          ],
        ],
        body: Object.entries(calBd)
          .sort(
            ([a], [b]) =>
              (calOrder.indexOf(a) === -1 ? 99 : calOrder.indexOf(a)) -
              (calOrder.indexOf(b) === -1 ? 99 : calOrder.indexOf(b))
          )
          .map(([n, d]) => [
            n,
            Math.round(d.kilos).toLocaleString('es-CL'),
            Math.round(d.othersKilos).toLocaleString('es-CL'),
            formatMoney(d.diff, { decimals: 0 }),
            d.kilos > 0 ? formatMoney(d.diff / d.kilos, { decimals: 2 }) : '-',
          ]),
        foot: (() => {
          const tK = Object.values(calBd).reduce((a, d) => a + d.kilos, 0);
          const tOK = Object.values(calBd).reduce(
            (a, d) => a + d.othersKilos,
            0
          );
          const tD = Object.values(calBd).reduce((a, d) => a + d.diff, 0);
          return [
            [
              'TOTAL',
              Math.round(tK).toLocaleString('es-CL'),
              Math.round(tOK).toLocaleString('es-CL'),
              formatMoney(tD, { decimals: 0 }),
              tK > 0 ? formatMoney(tD / tK, { decimals: 2 }) : '-',
            ],
          ];
        })(),
        ...miniStyles,
        footStyles: {
          fillColor: [charcoal.r, charcoal.g, charcoal.b],
          textColor: [255, 255, 255],
          fontStyle: 'bold' as const,
          fontSize: 7,
          cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
        },
        columnStyles: miniColStyles5,
        didParseCell: (data: any) => {
          colorDiffCell(data, 3);
          colorDiffCell(data, 4);
        },
      });

      // ═══ 5. SHIPMENT & PRODUCT BREAKDOWN (detail drilldown) ═══
      bY = (doc as any).lastAutoTable?.finalY + 12 || 200;
      doc.setFillColor(cherry.r, cherry.g, cherry.b);
      doc.rect(margin, bY - 3, 2.5, 2.5, 'F');
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(charcoal.r, charcoal.g, charcoal.b);
      doc.text('Shipment & Product Breakdown', margin + 5.5, bY);
      bY += 4;

      const parseDetailKey = (detailKey: string) => {
        const parts = detailKey.split('-');
        let variety = '';
        let caliber = '';
        let color = '';
        let restParts: string[] = [];

        if (isColorEnabled) {
          [variety, caliber, color, ...restParts] = parts;
        } else {
          [variety, caliber, ...restParts] = parts;
        }

        let format = '';
        let unit_weight = '';
        let label = '';

        if (isFormatEnabled && isLabelEnabled && restParts.length >= 3) {
          format = restParts[0];
          unit_weight = restParts[1].replace(/_/g, '.');
          label = restParts[2];
        } else if (isFormatEnabled && restParts.length >= 2) {
          format = restParts[0];
          unit_weight = restParts[1].replace(/_/g, '.');
        } else if (isLabelEnabled && restParts.length >= 1) {
          label = restParts[0];
        }

        return { variety, caliber, color, format, unit_weight, label };
      };

      const productCols: { key: 'color' | 'format' | 'label'; label: string }[] = [];
      if (isColorEnabled) productCols.push({ key: 'color', label: 'Color' });
      if (isFormatEnabled) productCols.push({ key: 'format', label: 'Format' });
      if (isLabelEnabled) productCols.push({ key: 'label', label: 'Label' });

      const tableRows: any[][] = [];
      let grandTotalDiff = 0;
      let grandTotalKilos = 0;

      Object.entries(activeData).forEach(([key, group]) => {
        const shipLabel = formatShipLabel(key);
        Object.entries(group.mainClient.details).forEach(
          ([detailKey, mainDetail]) => {
            const parsed = parseDetailKey(detailKey);

            let compKilos = 0;
            let compFob = 0;
            Object.values(group.comparisons).forEach((client) => {
              const d = client.details[detailKey];
              if (d) {
                compKilos += d.kilos;
                compFob += d.fob;
              }
            });

            const mainFobPerKg = mainDetail.fobPerKg;
            const compFobPerKg = compKilos > 0 ? compFob / compKilos : null;
            const diff =
              compFobPerKg != null ? mainFobPerKg - compFobPerKg : null;
            const totalDiffRow = diff != null ? diff * mainDetail.kilos : null;

            if (totalDiffRow != null) grandTotalDiff += totalDiffRow;
            grandTotalKilos += mainDetail.kilos;

            const productCells = productCols.map((c) => {
              if (c.key === 'format') {
                return parsed.unit_weight
                  ? `${parsed.format} ${parsed.unit_weight}`
                  : parsed.format || '-';
              }
              return parsed[c.key] || '-';
            });

            tableRows.push([
              shipLabel,
              parsed.variety,
              parsed.caliber || '-',
              ...productCells,
              mainDetail.kilos.toLocaleString('es-CL', {
                maximumFractionDigits: 0,
              }),
              formatMoney(mainFobPerKg, { decimals: 2 }),
              compFobPerKg != null
                ? formatMoney(compFobPerKg, { decimals: 2 })
                : '-',
              compKilos > 0
                ? compKilos.toLocaleString('es-CL', {
                    maximumFractionDigits: 0,
                  })
                : '-',
              diff != null ? formatMoney(diff, { decimals: 2 }) : '-',
              totalDiffRow != null
                ? formatMoney(totalDiffRow, { decimals: 0 })
                : '-',
            ]);
          }
        );
      });

      const baseHeadStart = ['Shipment', 'Variety', 'Size'];
      const productHead = productCols.map((c) => c.label);
      const numericHead = [
        'Own KG',
        `${currencyLabel}/KG`,
        `Comp. ${currencyLabel}/KG`,
        'Comp. KG',
        `Diff. ${currencyLabel}/KG`,
        'Total Diff.',
      ];
      const headRow = [...baseHeadStart, ...productHead, ...numericHead];
      const totalDiffColIdx = headRow.length - 1;
      const diffPerKgColIdx = headRow.length - 2;
      const ownKgColIdx = baseHeadStart.length + productHead.length;
      const footRow = headRow.map((_, i) => {
        if (i === 0) return 'TOTAL';
        if (i === ownKgColIdx)
          return grandTotalKilos.toLocaleString('es-CL', {
            maximumFractionDigits: 0,
          });
        if (i === totalDiffColIdx)
          return formatMoney(grandTotalDiff, { decimals: 0 });
        return '';
      });

      autoTable(doc, {
        startY: bY,
        margin: { left: margin, right: margin, bottom: 18 },
        head: [headRow],
        body: tableRows,
        foot: [footRow],
        styles: {
          fontSize: 6.5,
          cellPadding: { top: 2.2, right: 1.8, bottom: 2.2, left: 1.8 },
          lineColor: [borderC.r, borderC.g, borderC.b],
          lineWidth: 0.12,
          textColor: [slate.r, slate.g, slate.b],
        },
        headStyles: {
          fillColor: [charcoal.r, charcoal.g, charcoal.b],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 6,
          cellPadding: { top: 3, right: 1.8, bottom: 3, left: 1.8 },
        },
        footStyles: {
          fillColor: [cherry.r, cherry.g, cherry.b],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 6.5,
          cellPadding: { top: 3, right: 1.8, bottom: 3, left: 1.8 },
        },
        alternateRowStyles: { fillColor: [faintBg.r, faintBg.g, faintBg.b] },
        columnStyles: (() => {
          const styles: Record<number, any> = {
            0: { cellWidth: 28 },
            1: { cellWidth: 18 },
          };
          for (let i = ownKgColIdx; i < headRow.length; i++) {
            styles[i] = { ...(styles[i] || {}), halign: 'right' };
          }
          return styles;
        })(),
        didParseCell: (data: any) => {
          if (
            data.section === 'body' &&
            data.column.index === totalDiffColIdx
          ) {
            const raw = data.cell.raw as string;
            if (raw && raw !== '-') {
              data.cell.styles.textColor = raw.includes('-')
                ? [red.r, red.g, red.b]
                : [green.r, green.g, green.b];
              data.cell.styles.fontStyle = 'bold';
            }
          }
          if (
            data.section === 'body' &&
            data.column.index === diffPerKgColIdx
          ) {
            const raw = data.cell.raw as string;
            if (raw && raw !== '-') {
              data.cell.styles.textColor = raw.includes('-')
                ? [red.r, red.g, red.b]
                : [green.r, green.g, green.b];
            }
          }
        },
      });

      drawFooter();

      const fileName = `Certificate_Clients_${selectedClient.replace(/\s+/g, '_')}_${selectedSeason || ''}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  useEffect(() => {
    const fetchSettlements = async () => {
      if (!userProfile?.exporter_id) return;

      setIsLoadingSettlements(true);
      try {
        const allSettlements = await getSettlementsByExporterId(
          +userProfile.exporter_id,
          'approved',
          selectedSeason,
          selectedSpecies?.id,
          'all',
          ['*'],
          currentDivisionId()
        );

        setAllReceiverSettlements(allSettlements || []);

        // Price/exchange rate filter (sinister filter is applied only to main client later)
        const settlementsFiltered =
          priceType === 'FOB'
            ? allSettlements?.filter(
                (s) =>
                  s.total_freight_usd !== null &&
                  s.total_freight_usd > 0 &&
                  s.exchange_rate !== null
              ) || []
            : allSettlements?.filter((s) => s.exchange_rate !== null) || [];

        const filteredByShipmentType = settlementsFiltered.filter(
          (s) =>
            shipmentTypeFilter === 'both' ||
            s.ship_trip?.ship?.type === shipmentTypeFilter
        );

        setSettlements(filteredByShipmentType || []);
      } finally {
        setIsLoadingSettlements(false);
      }
    };
    fetchSettlements();
  }, [
    selectedSeason,
    priceType,
    userProfile?.exporter_id,
    shipmentTypeFilter,
    selectedSpecies,
  ]);

  useEffect(() => {
    logUserAction('view', 'page', 'settlements-comparative-clients');
  }, []);

  // Reset dependent fields when main client change
  useEffect(() => {
    setComparisonClients([]);
    setSelectedMainSettlements([]);
    setSelectedComparisonSettlements([]);
  }, [comparisonType, selectedSpecies]);

  // Reset comparison clients when the main client changes
  useEffect(() => {
    setComparisonClients([]);
    setSelectedComparisonSettlements([]);
    // Reset ship selection when in custom mode
    if (comparisonType === 'custom') {
      setSelectedMainSettlements([]);
    }
  }, [selectedClient, comparisonType]);

  const uniqueClients = [...new Set(filteredSettlements.map((s) => s.receiver))].sort(
    (a, b) => a.localeCompare(b)
  );
  const otherClients = uniqueClients.filter(
    (client) => comparisonType === 'custom' || client !== selectedClient
  );

  // Available local currencies for the selected main client
  // Currency filter predicate
  const currencyFilter = (s: Settlement) =>
    !useLocalCurrency ||
    !effectiveLocalCurrency ||
    s.local_currency === effectiveLocalCurrency;

  const mainClientSettlements = filteredSettlements.filter(
    (s) =>
      s.receiver === selectedClient &&
      currencyFilter(s) &&
      (sinisterFilter === 'all'
        ? true
        : sinisterFilter === 'sinister'
          ? s.is_sinister
          : !s.is_sinister)
  );
  const comparisonClientSettlements = filteredSettlements.filter((s) => {
    if (!currencyFilter(s)) return false;

    // Apply sinister filter for comparison clients
    if (sinisterFilterComparison !== 'all') {
      if (sinisterFilterComparison === 'sinister' && !s.is_sinister) return false;
      if (sinisterFilterComparison === 'non_sinister' && s.is_sinister) return false;
    }

    // When using 'all', exclude the main client
    if (comparisonClients.includes('all')) {
      return s.receiver !== selectedClient;
    }

    // When specific clients are selected
    if (comparisonClients.length > 0) {
      // If in custom mode and comparing with the same client
      if (
        comparisonType === 'custom' &&
        comparisonClients.includes(selectedClient) &&
        s.receiver === selectedClient
      ) {
        // In custom mode, allow using the same client but make sure we don't include
        // the settlements already selected for the main client to avoid confusion
        return !selectedMainSettlements.includes(s.id.toString());
      }
      // For other clients or non-custom mode
      return comparisonClients.includes(s.receiver);
    }

    return false;
  });

  const isComparisonReady =
    selectedClient &&
    comparisonClients.length > 0 &&
    (comparisonType === 'custom'
      ? selectedMainSettlements.length > 0 &&
        selectedComparisonSettlements.length > 0
      : true) &&
    (!useLocalCurrency ||
      mainClientCurrencies.length <= 1 ||
      selectedLocalCurrency !== '');

  // Add this effect to track changes in options
  useEffect(() => {
    if (summaryStatistics) {
      setIsOptionsChanged(true);
    }
  }, [
    selectedClient,
    comparisonClients,
    selectedMainSettlements,
    selectedComparisonSettlements,
    comparisonType,
    dateGroupingType,
    priceType,
    sinisterFilter,
    sinisterFilterComparison,
    shipmentTypeFilter,
    isFormatEnabled,
    isLabelEnabled,
    isColorEnabled,
    useLocalCurrency,
    selectedLocalCurrency,
    includeRevenuesMain,
    includeRevenuesComparison,
  ]);

  const handleGenerateComparison = () => {
    if (!isComparisonReady) return;

    setIsOptionsChanged(false);

    let selectedSettlements = [];

    if (comparisonType === 'custom') {
      // Para el cliente principal
      const mainSettlementsToCompare =
        selectedMainSettlements.length > 0
          ? (mainClientSettlements
              .filter((s) => selectedMainSettlements.includes(s.id.toString()))
              .map((s) => ({
                ...s,
                // Add a custom property to identify this as a main client selection
                _isMainSelection: true,
              })) as SettlementWithSelection[])
          : [];

      // Para los clientes de comparación
      const comparisonSettlementsToCompare =
        selectedComparisonSettlements.length > 0
          ? (comparisonClientSettlements
              .filter((s) =>
                selectedComparisonSettlements.includes(s.id.toString())
              )
              .map((s) => ({
                ...s,
                // Add a custom property to identify this as a comparison selection
                _isComparisonSelection: true,
              })) as SettlementWithSelection[])
          : [];

      selectedSettlements = [
        ...mainSettlementsToCompare,
        ...comparisonSettlementsToCompare,
      ];

      // Ensure we don't have duplicate settlements (could happen when comparing the same client)
      const uniqueSettlementIds = new Set();
      selectedSettlements = selectedSettlements.filter((s) => {
        if (uniqueSettlementIds.has(s.id)) {
          return false;
        }
        uniqueSettlementIds.add(s.id);
        return true;
      });
    } else {
      // En modo simple, usamos todas las liquidaciones sin filtrar
      selectedSettlements = [
        ...mainClientSettlements,
        ...comparisonClientSettlements,
      ];
    }

    // Si es modo custom, modificamos todas las liquidaciones para que tengan la misma nave
    if (comparisonType === 'custom') {
      const date = new Date();
      selectedSettlements = selectedSettlements.map((settlement) => ({
        ...settlement,
        date_sold: date,
        ship_trip: {
          ...settlement.ship_trip,
          eta: date,
          etd: date,
          ship: {
            ...settlement.ship_trip?.ship,
            name: 'COMPARACION_TOTAL',
            type: 'M',
          },
          id: 999999,
        },
      }));
    }

    let groupedData = ComparativeClientsUtility.groupSettlements(
      selectedSettlements,
      selectedClient,
      comparisonClients,
      isFormatEnabled,
      isLabelEnabled,
      isColorEnabled,
      priceType,
      dateGroupingType,
      comparisonType,
      useLocalCurrency,
      includeRevenuesMain,
      includeRevenuesComparison,
      includeMarketInAir
    );

    setMainClientDetails(groupedData.mainClientDetails);

    setSummaryStatistics(
      ComparativeClientsUtility.calculateSummaryStatistics(
        groupedData[comparisonType === 'custom' ? 'byShips' : comparisonType]
      ) as SummaryStatistics
    );
    setShipmentData(groupedData);

    // 👉 prepara los datos para la compensación
    const diffs = buildClaimDifferences(
      groupedData[comparisonType === 'custom' ? 'byShips' : comparisonType]
    );

    setPreparedClaimData({
      mainClient: mainClientSettlements[0] ?? null, // o la liquidación que prefieras
      differences: diffs,
      mainClientDetails: groupedData.mainClientDetails,
    });

    // Scroll to the results section after a short delay to ensure the results are rendered
    setTimeout(() => {
      if (actionsCardRef.current) {
        actionsCardRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  useEffect(() => {
    if (
      shipmentData &&
      shipmentData[comparisonType === 'custom' ? 'byShips' : comparisonType] &&
      Object.keys(
        shipmentData[comparisonType === 'custom' ? 'byShips' : comparisonType]
      ).length > 0
    ) {
      const stats = ComparativeClientsUtility.calculateSummaryStatistics(
        shipmentData[comparisonType === 'custom' ? 'byShips' : comparisonType]
      );
      setSummaryStatistics(stats as SummaryStatistics);
    }
  }, [shipmentData]);

  // Add useEffect to handle selectedSpecies changes
  useEffect(() => {
    // If selectedSpecies doesn't have color, disable color filtering
    if (selectedSpecies && !selectedSpecies.has_color) {
      setIsColorEnabled(false);
    }
  }, [selectedSpecies]);

  const handleSaveClaim = async () => {
    if (!summaryStatistics || !preparedClaimData.mainClient) return;

    try {
      setIsSavingClaim(true);

      if (
        !preparedClaimData.mainClient ||
        preparedClaimData.differences.length === 0
      ) {
        toast.error('Genera el comparativo primero');
        return;
      }

      const productDefinition = [
        'VARIETY',
        'CALIBER',
        isColorEnabled ? 'COLOR' : null,
        isFormatEnabled ? 'FORMAT' : null,
        isLabelEnabled ? 'LABEL' : null,
      ]
        .filter(Boolean)
        .join('-');

      // 0️⃣ claim header
      const claimRow = await createClaim({
        exporter_id: preparedClaimData.mainClient.exporter_id,
        receiver: preparedClaimData.mainClient.receiver,
        season: preparedClaimData.mainClient.season,
        notes: `Reclamo auto – diff total ${formatMoney(summaryStatistics!.totalFobDifference)}`,
        description: claimDescription || `Sin descripción`,
        total_claim_usd: -summaryStatistics!.totalFobDifference,
        currency: currencyLabel,
        date_grouping_type:
          comparisonType == 'custom' ? null : dateGroupingType,
        comparison_type: comparisonType,
        sinister_filter: sinisterFilter,
        sinister_filter_comparison: sinisterFilterComparison,
        shipment_type: shipmentTypeFilter,
        incoterm: priceType,
        comparison_clients: comparisonClients.includes('all')
          ? []
          : comparisonClients.filter((c) => c !== 'all'),
        product_definition: productDefinition,
        market_filter: marketFilter.length > 0 ? marketFilter : undefined,
        include_revenues_main: includeRevenuesMain,
        include_revenues_comparison: includeRevenuesComparison,
        include_market_in_air: includeMarketInAir,
        // Only set these for custom comparison type
        ...(comparisonType === 'custom'
          ? {
              main_references: selectedMainSettlements.map(Number),
              comparison_references: selectedComparisonSettlements.map(Number),
            }
          : {}),
      });

      // Create claim details
      const claimDetails = preparedClaimData.mainClientDetails
        .filter(
          (detail: MainClientDetailWithComparable) => detail.hasComparison
        )
        .map((detail: MainClientDetailWithComparable) => ({
          claim_id: claimRow.id,
          settlement_detail_id: detail.settlement_detail_id,
          comparable_fob_per_kg: detail.comparisonFobPerKg || 0,
          comparable_kilos: detail.comparableKilos,
          own_value_per_kg: detail.fobPerKg,
        }));

      await createClaimDetails(claimDetails);

      toast.success(
        `Comparativo #${claimRow.id} guardado. Revísalo en el menú Liquidaciones Clientes -> Compensaciones`
      );
      setShowClaimModal(false);
      setClaimDescription('');
    } catch (err) {
      console.error('Error saving claim:', err);
      toast.error('Error al guardar la compensación');
    } finally {
      setIsSavingClaim(false);
    }
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout>
        <Header
          title={t('Comparativo de Liquidaciones Clientes')}
          description={t(
            'Analiza y compara el rendimiento de liquidaciones entre diferentes clientes para obtener insights valiosos sobre el desempeño comercial.'
          )}
          titleAddon={
            <InformationTooltip>
              <div className='p-2'>
                <h3 className='font-semibold mb-2'>
                  {t('Descripción General:')}
                </h3>
                <p>
                  {t(
                    'Esta pantalla permite analizar y comparar el rendimiento de liquidaciones entre diferentes clientes. Los usuarios pueden aplicar diversos filtros y configuraciones para personalizar el análisis y obtener insights valiosos sobre el desempeño comercial.'
                  )}
                </p>
                <h3 className='font-semibold mt-4 mb-2'>
                  {t('Cómo se Calcula el Análisis:')}
                </h3>
                <ol className='list-decimal pl-4'>
                  <li>
                    <strong>{t('Filtrado de Datos:')}</strong>{' '}
                    {t(
                      'Primero, se filtran las liquidaciones seleccionadas por el usuario.'
                    )}
                  </li>
                  <li>
                    <strong>{t('Agrupación:')}</strong>{' '}
                    {t(
                      'Las liquidaciones se agrupan por naves, semanas o personalizada según la configuración elegida. Luego se agrupan los datos por variedad, calibre, color (opcional) y formato (opcional).'
                    )}
                  </li>
                  <li>
                    <strong>{t('Procesamiento de Grupos:')}</strong>{' '}
                    {t(
                      'Dentro de cada grupo, se calculan los totales de kilos y FOB para el cliente principal y los clientes de comparación.'
                    )}
                  </li>

                  <li>
                    <strong>{t('Comparación de Diferencias:')}</strong>{' '}
                    {t(
                      'Se comparan los valores por kilo entre el cliente principal y los clientes de comparación para encontrar diferencias.'
                    )}
                  </li>

                  <li>
                    <strong>{t('Exportación a Excel:')}</strong>{' '}
                    {t(
                      'Finalmente, se crea un archivo Excel con todos los datos calculados, listo para ser analizado más a fondo.'
                    )}
                  </li>
                </ol>
              </div>
            </InformationTooltip>
          }
        />

        <div className='space-y-6'>
          {/* Step 1: Configuration */}
          <Card className='p-4'>
            <CardBody>
              <div className='flex items-center gap-2 mb-4'>
                <div>
                  <div className='flex items-center gap-2'>
                    <h2 className='text-lg font-semibold'>
                      {t('1. Configuración Base')}
                    </h2>
                    <InformationTooltip>
                      <div className='p-2'>
                        <h3 className='font-semibold mb-2'>
                          {t('Configuración de Base')}
                        </h3>
                        <ul className='list-disc pl-4'>
                          <li>
                            <strong>{t('Temporada y Filtros')}</strong>{' '}
                            {t(
                              'Selecciona la temporada y aplica filtros de siniestros para refinar los datos.'
                            )}
                          </li>
                          <li>
                            <strong>{t('Tipo de Comparación')}</strong>{' '}
                            {t(
                              'Elige entre modos simples (por semanas o naves) o modo avanzado (personalizado) para un análisis detallado. Los modos simples agrupan automáticamente los datos, mientras que el modo personalizado permite una comparación más específica.'
                            )}
                          </li>
                          <li>
                            <strong>{t('Incoterm')}</strong>{' '}
                            {t(
                              'Selecciona entre FOB y CIF para determinar el tipo de precio a utilizar. Esto afecta cómo se calculan los valores de las liquidaciones.'
                            )}
                          </li>
                          <li>
                            <strong>{t('Tipo de Envío')}</strong>{' '}
                            {t(
                              'Filtra por tipo de flete: aéreo, marítimo, terrestre, o todos. Este filtro determina qué liquidaciones se incluyen en el análisis.'
                            )}
                          </li>
                        </ul>
                      </div>
                    </InformationTooltip>
                  </div>
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-4'>
                  <h3 className='text-sm font-medium text-default-600'>
                    {t('Temporada y Filtros')}
                  </h3>
                  <SeasonFilter />
                  <Select
                    label={t('Tipo de Flete')}
                    selectedKeys={[shipmentTypeFilter]}
                    size='sm'
                    items={[
                      { key: 'both', value: 'both', textValue: t('Todos') },
                      { key: 'A', value: 'A', textValue: t('Solo Aéreos') },
                      { key: 'M', value: 'M', textValue: t('Solo Marítimos') },
                      { key: 'T', value: 'T', textValue: t('Solo Terrestres') },
                    ]}
                    onSelectionChange={(keys) =>
                      setShipmentTypeFilter(
                        Array.from(keys)[0] as typeof shipmentTypeFilter
                      )
                    }
                  >
                    {(item) => (
                      <SelectItem
                        key={item.key}
                        value={item.value}
                        textValue={item.textValue}
                      >
                        {item.textValue}
                      </SelectItem>
                    )}
                  </Select>
                  <MarketFilter
                    data={settlements}
                    selectedMarket={marketFilter}
                    onSelectionChange={(value) => setMarketFilter(value)}
                    selectionMode='multiple'
                    defaultSelectAll
                    size='sm'
                    className='w-full'
                  />
                </div>
                <div className='space-y-4'>
                  <div className='flex items-center gap-2'>
                    <h3 className='text-sm font-medium text-default-600'>
                      {t('Tipo de Análisis')}
                    </h3>
                    <InformationTooltip>
                      <div className='p-2'>
                        <h3 className='font-semibold mb-2'>
                          {t('Tipos de Agrupación:')}
                        </h3>
                        <div className='space-y-3'>
                          <div>
                            <p className='font-semibold'>
                              {t('Por Semanas (Simple):')}
                            </p>
                            <ul className='list-disc pl-4 text-sm'>
                              <li>
                                {t(
                                  'Agrupa automáticamente todas las liquidaciones por semanas según la fecha seleccionada.'
                                )}
                              </li>
                              <li>
                                {t(
                                  'Permite seleccionar entre: fecha de venta, fecha de llegada (ETA) o fecha de salida (ETD).'
                                )}
                              </li>
                              <li>
                                {t(
                                  'Calcula los totales y promedios semanales para el cliente principal y los clientes a comparar.'
                                )}
                              </li>
                            </ul>
                          </div>
                          <div>
                            <p className='font-semibold'>
                              {t('Por Naves (Simple):')}
                            </p>
                            <ul className='list-disc pl-4 text-sm'>
                              <li>
                                {t(
                                  'Agrupa automáticamente todas las liquidaciones por cada viaje.'
                                )}
                              </li>
                              <li>
                                {t(
                                  'Todos los envíos aéreos y terrestres se agrupan por semana (AEREO W1, AEREO W2, TERRESTRE W1, TERRESTRE W2, etc.).'
                                )}
                              </li>
                              <li>
                                {t('Los envíos marítimos se agrupan por viaje')}
                              </li>
                            </ul>
                          </div>
                          <div>
                            <p className='font-semibold'>
                              {t('Personalizado (Avanzado):')}
                            </p>
                            <ul className='list-disc pl-4 text-sm'>
                              <li>
                                {t(
                                  'Permite seleccionar manualmente qué liquidaciones específicas comparar entre clientes.'
                                )}
                              </li>
                              <li>
                                {t(
                                  'Ofrece máxima flexibilidad para comparar naves específicas, referencias individuales o combinaciones.'
                                )}
                              </li>
                              <li>
                                {t(
                                  'Las selecciones se pueden hacer por nave o por referencia individual en ambos lados de la comparación.'
                                )}
                              </li>
                              <li>
                                {t(
                                  'Todos los datos seleccionados se agrupan en una única comparación llamada "COMPARACION_TOTAL".'
                                )}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </InformationTooltip>
                  </div>
                  <div className='relative'>
                    <Select
                      label={t('Tipo de Comparación')}
                      selectedKeys={[comparisonType]}
                      size='sm'
                      items={[
                        {
                          key: 'byWeeks',
                          value: 'byWeeks',
                          textValue: t('Por Semanas (Simple)'),
                          description: t(
                            'Agrupa automáticamente liquidaciones por semana según la fecha seleccionada (fecha de venta, fecha de llegada (ETA) o fecha de salida (ETD)). Considerando el lunes como primer día de la semana.'
                          ),
                        },
                        {
                          key: 'byShips',
                          value: 'byShips',
                          textValue: t('Por Naves (Simple)'),
                          description: t(
                            'Agrupa las liquidaciones marítimas por naves y las liquidaciones aéreas y terrestres por semana (AEREO W1, W2, TERRESTRE W1, W2, etc.). Considerando el lunes como primer día de la semana.'
                          ),
                        },
                        {
                          key: 'custom',
                          value: 'custom',
                          textValue: t('Personalizado (Avanzado)'),
                          description: t(
                            'Permite seleccionar manualmente liquidaciones específicas por nave o referencia. Todo se agrupa en una única comparación detallada y se compara todas las liquidaciones seleccionadas.'
                          ),
                        },
                      ]}
                      onChange={(e) => {
                        const newValue = e.target
                          .value as typeof comparisonType;
                        setComparisonType(newValue);

                        // Reset all selections when changing modes
                        if (newValue !== 'custom') {
                          setSelectedMainSettlements(['all']);
                          setSelectedComparisonSettlements(['all']);
                        } else {
                          setSelectedMainSettlements([]);
                          setSelectedComparisonSettlements([]);
                          setComparisonClients([]);
                        }

                        // Reset format and label options
                        setIsFormatEnabled(true);
                        setIsLabelEnabled(false);
                        setSummaryStatistics(null);
                        setShipmentData(null);
                      }}
                    >
                      {(item) => (
                        <SelectItem
                          key={item.key}
                          value={item.value}
                          textValue={item.textValue}
                          description={item.description}
                        >
                          {item.textValue}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                  <Select
                    label={t('Incoterm')}
                    selectedKeys={[priceType]}
                    size='sm'
                    items={[
                      {
                        key: 'FOB',
                        value: 'FOB',
                        textValue: 'FOB',
                        description: t(
                          'Free On Board: Análisis basado en el precio habiendo descontado el flete.'
                        ),
                      },
                      {
                        key: 'CIF',
                        value: 'CIF',
                        textValue: 'CIF',
                        description: t(
                          'Cost, Insurance & Freight: Análisis basado en el precio sin descontar el flete.'
                        ),
                      },
                    ]}
                    onChange={(e) =>
                      setPriceType(e.target.value as 'FOB' | 'CIF')
                    }
                  >
                    {(item) => (
                      <SelectItem
                        key={item.key}
                        value={item.value}
                        textValue={item.textValue}
                        description={item.description}
                      >
                        {item.textValue}
                      </SelectItem>
                    )}
                  </Select>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Step 2: Client Selection */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* Main Client */}
            <Card className='p-4'>
              <CardBody>
                <div className='flex items-center gap-2 '>
                  <div className='flex items-center gap-2'>
                    <h2 className='text-lg font-semibold'>
                      {t('2. Cliente Principal')}
                    </h2>
                    {/* <InformationTooltip>
                      <div className='p-2'>
                        <h3 className='font-semibold mb-2'>
                          Selección de Clientes:
                        </h3>
                        <ul className='list-disc pl-4'>
                          <li>
                            <strong>Cliente Principal:</strong> Selecciona el
                            cliente base para la comparación. En modo
                            personalizado, selecciona referencias específicas
                            para un análisis más detallado.
                          </li>
                        </ul>
                      </div>
                    </InformationTooltip> */}
                  </div>
                </div>
                <p className='text-default-500 text-sm mb-4'>
                  {t('Selecciona el cliente principal para la comparación.')}
                </p>

                <div className='space-y-4'>
                  {isLoadingSettlements ? (
                    <LoadingState label={t('Cargando clientes...')} />
                  ) : uniqueClients.length === 0 ? (
                    <EmptyState
                      icon='tabler:users'
                      title={t(
                        'No se encontraron clientes para la temporada y especie seleccionada.'
                      )}
                    />
                  ) : (
                    <Select
                      label={t('Cliente Principal')}
                      placeholder={t('Selecciona el cliente')}
                      value={selectedClient}
                      className='w-full'
                      size='sm'
                      items={uniqueClients.map((client) => ({
                        key: client,
                        value: client,
                        textValue: client,
                      }))}
                      onChange={(e) => setSelectedClient(e.target.value)}
                    >
                      {(item) => (
                        <SelectItem
                          key={item.key}
                          value={item.value}
                          textValue={item.textValue}
                        >
                          {item.textValue}
                        </SelectItem>
                      )}
                    </Select>
                  )}

                  {selectedClient && (
                    <Select
                      label={t('Filtro de Siniestros')}
                      selectedKeys={[sinisterFilter]}
                      size='sm'
                      items={[
                        { key: 'all', value: 'all', textValue: t('Todos') },
                        {
                          key: 'sinister',
                          value: 'sinister',
                          textValue: t('Solo Siniestrados'),
                        },
                        {
                          key: 'non_sinister',
                          value: 'non_sinister',
                          textValue: t('Solo No Siniestrados'),
                        },
                      ]}
                      onSelectionChange={(keys) => {
                        setSinisterFilter(
                          Array.from(keys)[0] as typeof sinisterFilter
                        );
                        setSelectedMainSettlements([]);
                      }}
                    >
                      {(item) => (
                        <SelectItem
                          key={item.key}
                          value={item.value}
                          textValue={item.textValue}
                        >
                          {item.textValue}
                        </SelectItem>
                      )}
                    </Select>
                  )}

                  {selectedClient && (
                    <div className='flex items-center gap-3 mt-2'>
                      <Switch
                        size='sm'
                        isSelected={useLocalCurrency}
                        onValueChange={(val) => {
                          setUseLocalCurrency(val);
                          if (!val) setSelectedLocalCurrency('');
                        }}
                      >
                        <span className='text-sm'>
                          {t('Comparativa en moneda local')}
                        </span>
                      </Switch>
                      {useLocalCurrency && mainClientCurrencies.length > 1 && (
                        <Select
                          size='sm'
                          label={t('Moneda')}
                          className='max-w-[140px]'
                          selectedKeys={
                            selectedLocalCurrency ? [selectedLocalCurrency] : []
                          }
                          onChange={(e) =>
                            setSelectedLocalCurrency(e.target.value)
                          }
                        >
                          {mainClientCurrencies.map((currency) => (
                            <SelectItem key={currency} value={currency}>
                              {currency}
                            </SelectItem>
                          ))}
                        </Select>
                      )}
                      {useLocalCurrency && effectiveLocalCurrency && (
                        <span className='text-xs text-default-400'>
                          {mainClientSettlements.length} {t('liquidaciones en')}{' '}
                          {effectiveLocalCurrency}
                        </span>
                      )}
                    </div>
                  )}

                  {selectedClient && (
                    <div className='flex items-center gap-3 mt-2'>
                      <Switch
                        size='sm'
                        isSelected={includeRevenuesMain}
                        onValueChange={setIncludeRevenuesMain}
                      >
                        <span className='text-sm'>
                          {t('Considerar ingresos adicionales')}
                        </span>
                      </Switch>
                    </div>
                  )}

                  {selectedClient && comparisonType === 'custom' && (
                    <div className='space-y-4'>
                      <p className='text-sm text-default-400'>
                        {t('Selecciona las referencias que deseas comparar.')}
                      </p>
                      <Tabs
                        key={`client-tabs-${selectedClient}-${sinisterFilter}`}
                        aria-label={t('Selección de Referencias')}
                        className=''
                        size='sm'
                        defaultSelectedKey='byShip'
                        onSelectionChange={() => {
                          setSelectedMainSettlements([]);
                        }}
                      >
                        <Tab key='byShip' title={t('Por Nave')}>
                          <div className='mt-2'>
                            <Select
                              label={t('Seleccionar Nave')}
                              placeholder={t('Elige una nave')}
                              className='w-full'
                              size='sm'
                              selectionMode='multiple'
                              items={mainClientSettlements
                                .filter((s) => s.ship_trip?.ship?.name)
                                .reduce(
                                  (unique, settlement) => {
                                    const identifier =
                                      settlement.ship_trip?.ship?.type == 'A'
                                        ? `AEREO-${getWeekKey(
                                            settlement.date_sold || ''
                                          )}`
                                        : settlement.ship_trip?.ship?.type ==
                                            'T'
                                          ? `TERRESTRE-${getWeekKey(
                                              settlement.date_sold || ''
                                            )}`
                                          : settlement.ship_trip_id?.toString();

                                    // Si este identificador ya existe, no lo agregamos
                                    if (
                                      !unique.some(
                                        (item) => item.key === identifier
                                      )
                                    ) {
                                      const trip = settlement.ship_trip;
                                      const shipType = trip?.ship?.type;
                                      const baseName =
                                        shipType == 'A'
                                          ? `AEREO W${
                                              (
                                                getWeekKey(
                                                  settlement.date_sold || ''
                                                ) || ''
                                              ).split('W')[1] || ''
                                            }`
                                          : shipType == 'T'
                                            ? `TERRESTRE W${
                                                (
                                                  getWeekKey(
                                                    settlement.date_sold || ''
                                                  ) || ''
                                                ).split('W')[1] || ''
                                              }`
                                            : trip?.ship?.name || 'Sin Nave';
                                      const isGroup =
                                        shipType == 'A' || shipType == 'T';
                                      const etd = trip?.etd
                                        ? new Date(
                                            trip.etd
                                          ).toLocaleDateString()
                                        : '-';
                                      const eta = trip?.eta
                                        ? new Date(
                                            trip.eta
                                          ).toLocaleDateString()
                                        : '-';
                                      const pod =
                                        trip?.market?.name || '-';
                                      unique.push({
                                        key: identifier,
                                        value: identifier,
                                        textValue: baseName,
                                        description: isGroup
                                          ? undefined
                                          : `ETD: ${etd} | ETA: ${eta} | POD: ${pod}`,
                                      });
                                    }
                                    return unique;
                                  },
                                  [] as Array<{
                                    key: string;
                                    value: string;
                                    textValue: string;
                                    description?: string;
                                  }>
                                )
                                .sort((a, b) => {
                                  // Sort AEREO by week number if both are AEREO
                                  if (
                                    a.textValue.startsWith('AEREO') &&
                                    b.textValue.startsWith('AEREO')
                                  ) {
                                    // La clave lleva el año ISO (AEREO-2026-W53)
                                    return sortWeekKeys(a.key, b.key);
                                  }

                                  // Sort TERRESTRE by week number if both are TERRESTRE
                                  if (
                                    a.textValue.startsWith('TERRESTRE') &&
                                    b.textValue.startsWith('TERRESTRE')
                                  ) {
                                    // La clave lleva el año ISO (AEREO-2026-W53)
                                    return sortWeekKeys(a.key, b.key);
                                  }

                                  // AEREO comes first
                                  if (a.textValue.startsWith('AEREO'))
                                    return -1;
                                  if (b.textValue.startsWith('AEREO')) return 1;

                                  // TERRESTRE comes last
                                  if (a.textValue.startsWith('TERRESTRE'))
                                    return 1;
                                  if (b.textValue.startsWith('TERRESTRE'))
                                    return -1;

                                  // Sort maritimos alphabetically (they're in the middle)
                                  return a.textValue.localeCompare(b.textValue);
                                })}
                              onSelectionChange={(keys) => {
                                const selectedKeys = Array.from(
                                  keys as Set<string>
                                );
                                let selectedSettlements: string[] = [];

                                selectedKeys.forEach((shipIdentifier) => {
                                  if (shipIdentifier.startsWith('AEREO-')) {
                                    // For AEREO, select all references from that specific week
                                    const weekKey =
                                      shipIdentifier.split('AEREO-')[1];
                                    selectedSettlements =
                                      selectedSettlements.concat(
                                        mainClientSettlements
                                          .filter(
                                            (s) =>
                                              s.ship_trip?.ship?.type == 'A' &&
                                              (getWeekKey(s.date_sold || '') ||
                                                '') === weekKey
                                          )
                                          .map((s) => s.id.toString())
                                      );
                                  } else if (
                                    shipIdentifier.startsWith('TERRESTRE-')
                                  ) {
                                    // For TERRESTRE, select all references from that specific week
                                    const weekKey =
                                      shipIdentifier.split('TERRESTRE-')[1];
                                    selectedSettlements =
                                      selectedSettlements.concat(
                                        mainClientSettlements
                                          .filter(
                                            (s) =>
                                              s.ship_trip?.ship?.type == 'T' &&
                                              (getWeekKey(s.date_sold || '') ||
                                                '') === weekKey
                                          )
                                          .map((s) => s.id.toString())
                                      );
                                  } else {
                                    // For regular ships, select all references from that ship
                                    selectedSettlements =
                                      selectedSettlements.concat(
                                        mainClientSettlements
                                          .filter(
                                            (s) =>
                                              s.ship_trip_id?.toString() ===
                                              shipIdentifier
                                          )
                                          .map((s) => s.id.toString())
                                      );
                                  }
                                });

                                setSelectedMainSettlements(selectedSettlements);
                              }}
                            >
                              {(item) => (
                                <SelectItem
                                  key={item.key}
                                  value={item.value}
                                  description={item.description}
                                >
                                  {item.textValue}
                                </SelectItem>
                              )}
                            </Select>
                          </div>
                        </Tab>
                        <Tab key='byReference' title={t('Por Referencia')}>
                          <div className='mt-2'>
                            <Select
                              label={t('Seleccionar Referencias')}
                              placeholder={t('Elige las referencias')}
                              selectionMode='multiple'
                              className='w-full'
                              size='sm'
                              selectedKeys={selectedMainSettlements}
                              items={mainClientSettlements
                                .map((settlement) => {
                                  const trip = settlement.ship_trip;
                                  const etd = trip?.etd
                                    ? new Date(trip.etd).toLocaleDateString()
                                    : '-';
                                  const eta = trip?.eta
                                    ? new Date(trip.eta).toLocaleDateString()
                                    : '-';
                                  const pod = trip?.market?.name || '-';
                                  return {
                                    key: settlement.id.toString(),
                                    value: settlement.id.toString(),
                                    textValue: `Ref: ${
                                      settlement.reference_number ||
                                      'Sin Referencia'
                                    } - ${
                                      trip?.ship?.type === 'A'
                                        ? `AEREO W${
                                            (
                                              getWeekKey(
                                                settlement.date_sold || ''
                                              ) || ''
                                            ).split('W')[1] || ''
                                          }`
                                        : trip?.ship?.type === 'T'
                                          ? `TERRESTRE W${
                                              (
                                                getWeekKey(
                                                  settlement.date_sold || ''
                                                ) || ''
                                              ).split('W')[1] || ''
                                            }`
                                          : trip?.ship?.name || 'Sin Nave'
                                    }`,
                                    description: `ETD: ${etd} | ETA: ${eta} | POD: ${pod}`,
                                    weekKey:
                                      trip?.ship?.type === 'A' ||
                                      trip?.ship?.type === 'T'
                                        ? getWeekKey(
                                            settlement.date_sold || ''
                                          ) || ''
                                        : null,
                                    date: settlement.date_sold || '',
                                  };
                                })
                                .sort((a, b) => {
                                  if (a.weekKey && b.weekKey) {
                                    return sortWeekKeys(a.weekKey, b.weekKey);
                                  }
                                  if (!a.weekKey && !b.weekKey) {
                                    return (
                                      new Date(a.date).getTime() -
                                      new Date(b.date).getTime()
                                    );
                                  }
                                  return a.weekKey ? -1 : 1;
                                })}
                              onSelectionChange={(keys) => {
                                setSelectedMainSettlements(
                                  Array.from(keys as Set<string>)
                                );
                              }}
                            >
                              {(item) => (
                                <SelectItem
                                  key={item.key}
                                  value={item.value}
                                  description={item.description}
                                >
                                  {item.textValue}
                                </SelectItem>
                              )}
                            </Select>
                          </div>
                        </Tab>
                      </Tabs>
                      <div className='text-sm text-default-400 mt-2'>
                        {selectedMainSettlements.length}{' '}
                        {t('referencias seleccionadas')}
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Comparison Clients */}
            <Card className='p-4'>
              <CardBody>
                <div className='flex items-center gap-2 mb-4'>
                  <div>
                    <div className='flex items-center gap-2'>
                      <h2 className='text-lg font-semibold'>
                        {t('3. Clientes a Comparar')}
                      </h2>
                      {/* <InformationTooltip>
                        <div className='p-2'>
                          <li>
                            <strong>Clientes a Comparar:</strong> Selecciona los
                            clientes para la comparación y, en modo
                            personalizado, las referencias específicas.
                          </li>
                          {comparisonType === 'custom' && (
                            <li className='mt-2'>
                              <strong>Nota:</strong> En modo personalizado,
                              puedes comparar referencias diferentes del mismo
                              cliente. El cliente principal aparecerá marcado
                              como "(Mismo Cliente)" en la lista de selección.
                            </li>
                          )}
                        </div>
                      </InformationTooltip> */}
                    </div>
                    <p className='text-default-500 text-sm'>
                      {t('Selecciona los clientes contra los cuales comparar.')}
                    </p>
                  </div>
                </div>

                <div className='space-y-4'>
                  <Select
                    label={t('Clientes a Comparar')}
                    placeholder={t('Selecciona los clientes')}
                    selectionMode='multiple'
                    className='w-full'
                    size='sm'
                    selectedKeys={comparisonClients}
                    items={[
                      {
                        key: 'all',
                        value: 'all',
                        textValue: t('Todos los clientes'),
                      },
                      ...otherClients.map((client) => ({
                        key: client,
                        value: client,
                        textValue:
                          client +
                          (client === selectedClient &&
                          comparisonType === 'custom'
                            ? t(' (Mismo Cliente)')
                            : ''),
                      })),
                    ]}
                    onSelectionChange={(keys) => {
                      const selectedKeys = Array.from(keys as Set<string>);
                      if (selectedKeys.includes('all')) {
                        if (selectedKeys[selectedKeys.length - 1] === 'all') {
                          setComparisonClients(['all']);
                        } else {
                          const filteredKeys = selectedKeys.filter(
                            (key) => key !== 'all'
                          );
                          setComparisonClients(filteredKeys);
                        }
                      } else {
                        setComparisonClients(selectedKeys);
                      }

                      setSummaryStatistics(null);
                      setShipmentData(null);
                    }}
                  >
                    {(item) => (
                      <SelectItem
                        key={item.key}
                        value={item.value}
                        textValue={item.textValue}
                      >
                        {item.textValue}
                      </SelectItem>
                    )}
                  </Select>
                  {comparisonClients.length > 0 && (
                    <Select
                      label={t('Filtro de Siniestros')}
                      selectedKeys={[sinisterFilterComparison]}
                      size='sm'
                      items={[
                        { key: 'all', value: 'all', textValue: t('Todos') },
                        {
                          key: 'sinister',
                          value: 'sinister',
                          textValue: t('Solo Siniestrados'),
                        },
                        {
                          key: 'non_sinister',
                          value: 'non_sinister',
                          textValue: t('Solo No Siniestrados'),
                        },
                      ]}
                      onSelectionChange={(keys) => {
                        setSinisterFilterComparison(
                          Array.from(keys)[0] as typeof sinisterFilterComparison
                        );
                        setSelectedComparisonSettlements([]);
                      }}
                    >
                      {(item) => (
                        <SelectItem
                          key={item.key}
                          value={item.value}
                          textValue={item.textValue}
                        >
                          {item.textValue}
                        </SelectItem>
                      )}
                    </Select>
                  )}
                  {comparisonClients.length > 0 && (
                    <div className='flex items-center gap-3 mt-2'>
                      <Switch
                        size='sm'
                        isSelected={includeRevenuesComparison}
                        onValueChange={setIncludeRevenuesComparison}
                      >
                        <span className='text-sm'>
                          {t('Considerar ingresos adicionales')}
                        </span>
                      </Switch>
                    </div>
                  )}
                  {comparisonClients.length > 0 &&
                    comparisonType === 'custom' && (
                      <div className='space-y-4'>
                        <p className='text-sm text-default-400'>
                          {t('Selecciona las referencias que deseas comparar.')}
                        </p>
                        <Tabs
                          key={`comparison-tabs-${selectedClient}-${comparisonClients.join('-')}-${sinisterFilterComparison}`}
                          aria-label={t('Selección de Referencias a Comparar')}
                          className=''
                          size='sm'
                          defaultSelectedKey='byShip'
                          onSelectionChange={() => {
                            setSelectedComparisonSettlements([]);
                          }}
                        >
                          <Tab key='byShip' title={t('Por Nave')}>
                            <div className='mt-2'>
                              <Select
                                label={t('Seleccionar Nave')}
                                placeholder={t('Elige una nave')}
                                className='w-full'
                                size='sm'
                                selectionMode='multiple'
                                items={comparisonClientSettlements
                                  .filter((s) => s.ship_trip?.ship?.name)
                                  .reduce(
                                    (unique, settlement) => {
                                      const identifier =
                                        settlement.ship_trip?.ship?.type === 'A'
                                          ? `AEREO-${getWeekKey(
                                              settlement.date_sold || ''
                                            )}`
                                          : settlement.ship_trip?.ship?.type ===
                                              'T'
                                            ? `TERRESTRE-${getWeekKey(
                                                settlement.date_sold || ''
                                              )}`
                                            : settlement.ship_trip_id?.toString();

                                      // Si este identificador ya existe, no lo agregamos
                                      if (
                                        !unique.some(
                                          (item) => item.key === identifier
                                        )
                                      ) {
                                        const trip = settlement.ship_trip;
                                        const shipType = trip?.ship?.type;
                                        const baseName =
                                          shipType === 'A'
                                            ? `AEREO W${
                                                (
                                                  getWeekKey(
                                                    settlement.date_sold || ''
                                                  ) || ''
                                                ).split('W')[1] || ''
                                              }`
                                            : shipType === 'T'
                                              ? `TERRESTRE W${
                                                  (
                                                    getWeekKey(
                                                      settlement.date_sold || ''
                                                    ) || ''
                                                  ).split('W')[1] || ''
                                                }`
                                              : trip?.ship?.name || 'Sin Nave';
                                        const isGroup =
                                          shipType === 'A' || shipType === 'T';
                                        const etd = trip?.etd
                                          ? new Date(
                                              trip.etd
                                            ).toLocaleDateString()
                                          : '-';
                                        const eta = trip?.eta
                                          ? new Date(
                                              trip.eta
                                            ).toLocaleDateString()
                                          : '-';
                                        const pod =
                                          trip?.market?.name || '-';
                                        unique.push({
                                          key: identifier,
                                          value: identifier,
                                          textValue: baseName,
                                          description: isGroup
                                            ? undefined
                                            : `ETD: ${etd} | ETA: ${eta} | POD: ${pod}`,
                                        });
                                      }
                                      return unique;
                                    },
                                    [] as Array<{
                                      key: string;
                                      value: string;
                                      textValue: string;
                                      description?: string;
                                    }>
                                  )
                                  .sort((a, b) => {
                                    // Sort AEREO by week number if both are AEREO
                                    if (
                                      a.textValue.startsWith('AEREO') &&
                                      b.textValue.startsWith('AEREO')
                                    ) {
                                      const weekA = parseInt(
                                        a.textValue.split('W')[1]
                                      );
                                      const weekB = parseInt(
                                        b.textValue.split('W')[1]
                                      );
                                      return weekA - weekB;
                                    }

                                    // Sort TERRESTRE by week number if both are TERRESTRE
                                    if (
                                      a.textValue.startsWith('TERRESTRE') &&
                                      b.textValue.startsWith('TERRESTRE')
                                    ) {
                                      const weekA = parseInt(
                                        a.textValue.split('W')[1]
                                      );
                                      const weekB = parseInt(
                                        b.textValue.split('W')[1]
                                      );
                                      return weekA - weekB;
                                    }

                                    // AEREO comes first
                                    if (a.textValue.startsWith('AEREO'))
                                      return -1;
                                    if (b.textValue.startsWith('AEREO'))
                                      return 1;

                                    // TERRESTRE comes last
                                    if (a.textValue.startsWith('TERRESTRE'))
                                      return 1;
                                    if (b.textValue.startsWith('TERRESTRE'))
                                      return -1;

                                    // Sort maritimos alphabetically (they're in the middle)
                                    return a.textValue.localeCompare(
                                      b.textValue
                                    );
                                  })}
                                onSelectionChange={(keys) => {
                                  const selectedKeys = Array.from(
                                    keys as Set<string>
                                  );
                                  let selectedSettlements: string[] = [];

                                  selectedKeys.forEach((shipIdentifier) => {
                                    if (shipIdentifier.startsWith('AEREO-')) {
                                      // For AEREO, select all references from that specific week
                                      const weekKey =
                                        shipIdentifier.split('AEREO-')[1];
                                      selectedSettlements =
                                        selectedSettlements.concat(
                                          comparisonClientSettlements
                                            .filter(
                                              (s) =>
                                                s.ship_trip?.ship?.type ===
                                                  'A' &&
                                                (getWeekKey(
                                                  s.date_sold || ''
                                                ) || '') === weekKey
                                            )
                                            .map((s) => s.id.toString())
                                        );
                                    } else if (
                                      shipIdentifier.startsWith('TERRESTRE-')
                                    ) {
                                      // For terrestre, select all references from that specific week
                                      const weekKey =
                                        shipIdentifier.split('TERRESTRE-')[1];
                                      selectedSettlements =
                                        selectedSettlements.concat(
                                          comparisonClientSettlements
                                            .filter(
                                              (s) =>
                                                s.ship_trip?.ship?.type ===
                                                  'T' &&
                                                (getWeekKey(
                                                  s.date_sold || ''
                                                ) || '') === weekKey
                                            )
                                            .map((s) => s.id.toString())
                                        );
                                    } else {
                                      // For regular ships, select all references from that ship
                                      selectedSettlements =
                                        selectedSettlements.concat(
                                          comparisonClientSettlements
                                            .filter(
                                              (s) =>
                                                s.ship_trip_id?.toString() ===
                                                shipIdentifier
                                            )
                                            .map((s) => s.id.toString())
                                        );
                                    }
                                  });

                                  setSelectedComparisonSettlements(
                                    selectedSettlements
                                  );
                                }}
                              >
                                {(item) => (
                                  <SelectItem
                                    key={item.key}
                                    value={item.value}
                                    description={item.description}
                                  >
                                    {item.textValue}
                                  </SelectItem>
                                )}
                              </Select>
                            </div>
                          </Tab>
                          <Tab key='byReference' title={t('Por Referencia')}>
                            <div className='mt-2'>
                              <Select
                                label={t('Seleccionar Referencias')}
                                placeholder={t('Elige las referencias')}
                                selectionMode='multiple'
                                className='w-full'
                                size='sm'
                                selectedKeys={selectedComparisonSettlements}
                                items={comparisonClientSettlements
                                  .map((settlement) => {
                                    const trip = settlement.ship_trip;
                                    const etd = trip?.etd
                                      ? new Date(trip.etd).toLocaleDateString()
                                      : '-';
                                    const eta = trip?.eta
                                      ? new Date(trip.eta).toLocaleDateString()
                                      : '-';
                                    const pod = trip?.market?.name || '-';
                                    return {
                                      key: settlement.id.toString(),
                                      value: settlement.id.toString(),
                                      textValue: `${settlement.receiver} - Ref: ${
                                        settlement.reference_number ||
                                        'Sin Referencia'
                                      } - ${
                                        trip?.ship?.type == 'A'
                                          ? `AEREO W${
                                              (
                                                getWeekKey(
                                                  settlement.date_sold || ''
                                                ) || ''
                                              ).split('W')[1] || ''
                                            }`
                                          : trip?.ship?.type == 'T'
                                            ? `TERRESTRE W${
                                                (
                                                  getWeekKey(
                                                    settlement.date_sold || ''
                                                  ) || ''
                                                ).split('W')[1] || ''
                                              }`
                                            : trip?.ship?.name || 'Sin Nave'
                                      }`,
                                      description: `ETD: ${etd} | ETA: ${eta} | POD: ${pod}`,
                                      weekKey:
                                        trip?.ship?.type == 'A' ||
                                        trip?.ship?.type == 'T'
                                          ? getWeekKey(
                                              settlement.date_sold || ''
                                            ) || ''
                                          : null,
                                      date: settlement.date_sold || '',
                                    };
                                  })
                                  .sort((a, b) => {
                                    if (a.weekKey && b.weekKey) {
                                      return sortWeekKeys(a.weekKey, b.weekKey);
                                    }
                                    if (!a.weekKey && !b.weekKey) {
                                      return (
                                        new Date(a.date).getTime() -
                                        new Date(b.date).getTime()
                                      );
                                    }
                                    return a.weekKey ? -1 : 1;
                                  })}
                                onSelectionChange={(keys) => {
                                  setSelectedComparisonSettlements(
                                    Array.from(keys as Set<string>)
                                  );
                                }}
                              >
                                {(item) => (
                                  <SelectItem
                                    key={item.key}
                                    value={item.value}
                                    description={item.description}
                                  >
                                    {item.textValue}
                                  </SelectItem>
                                )}
                              </Select>
                            </div>
                          </Tab>
                        </Tabs>
                        <div className='text-sm text-default-400 mt-2'>
                          {selectedComparisonSettlements.length}{' '}
                          {t('referencias seleccionadas')}
                        </div>
                      </div>
                    )}
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Step 4: Options and Actions */}
          {selectedClient && comparisonClients.length > 0 && (
            <Card className='p-4'>
              <CardBody>
                <div className='flex items-center gap-2 mb-4'>
                  <div>
                    <div className='flex items-center gap-2'>
                      <h2 className='text-lg font-semibold'>
                        {t('4. Productos Comparables')}
                      </h2>
                      {/* <InformationTooltip>
                      <div className='p-2'>
                        <h3 className='font-semibold mb-2'>
                          Productos Comparables:
                        </h3>
                        <ul className='list-disc pl-4'>
                          <li>
                            <strong>Definir productos comparables por:</strong> Permite seleccionar las características que determinan si dos productos son comparables entre sí, como variedad, calibre, color, formato y/o etiqueta.
                          </li>
                          
                        </ul>
                      </div>
                    </InformationTooltip> */}
                    </div>
                    <p className='text-default-500 text-sm'>
                      {t(
                        'Selecciona las características que hacen que dos productos sean comparables entre sí.'
                      )}
                    </p>
                  </div>
                </div>

                <div className='flex items-center gap-6'>
                  <div className='flex-1'>
                    <div className='bg-default-50 rounded-xl p-4 border border-default-200'>
                      <div className='flex items-center gap-8'>
                        {(comparisonType == 'byWeeks' ||
                          comparisonType == 'byShips') && (
                          <div>
                            <p className='text-sm text-default-500 mb-2'>
                              {comparisonType == 'byWeeks'
                                ? t('Usar semana de:')
                                : t(
                                    'Agrupar aéreos y terrestres por semana de:'
                                  )}
                            </p>
                            <RadioGroup
                              orientation='horizontal'
                              value={dateGroupingType}
                              onValueChange={(value) =>
                                setDateGroupingType(
                                  value as 'date_sold' | 'ship_eta'
                                )
                              }
                              classNames={{
                                wrapper: 'gap-6',
                              }}
                            >
                              <Radio value='date_sold'>
                                {t('Fecha de Venta')}
                              </Radio>

                              <Radio value='ship_eta'>
                                {t('ETA de la Nave')}
                              </Radio>
                              <Radio value='ship_etd'>
                                {t('ETD de la Nave')}
                              </Radio>
                            </RadioGroup>
                            <Switch
                              className='mt-2'
                              size='sm'
                              isSelected={includeMarketInAir}
                              onValueChange={(value) => {
                                setIncludeMarketInAir(value);
                                setIsOptionsChanged(true);
                              }}
                            >
                              <span className='text-sm'>
                                {t(
                                  'Considerar Mercado de Venta en Agrupación Aéreos y Terrestres'
                                )}
                              </span>
                            </Switch>
                          </div>
                        )}
                        {comparisonType == 'byShips' && (
                          <div>
                            {/* <p className='text-sm text-default-500 mb-2'>
                              {t('Agrupación Aéreos y Terrestres')}
                            </p> */}
                          </div>
                        )}
                        <div>
                          <p className='text-sm text-default-500 mb-2'>
                            {t('Definir productos comparables por:')}
                          </p>
                          <div className='flex gap-6'>
                            <Checkbox isSelected isDisabled>
                              {t('Variedad')}
                            </Checkbox>
                            <Checkbox isSelected isDisabled>
                              {t('Calibre')}
                            </Checkbox>
                            {/* Only show color checkbox if the species has color */}
                            {selectedSpecies?.has_color && (
                              <Checkbox
                                isSelected={isColorEnabled}
                                onValueChange={() =>
                                  setIsColorEnabled(!isColorEnabled)
                                }
                              >
                                {t('Color')}
                              </Checkbox>
                            )}
                            <Checkbox
                              isSelected={isFormatEnabled}
                              onValueChange={() =>
                                setIsFormatEnabled(!isFormatEnabled)
                              }
                            >
                              {t('Formato')}
                            </Checkbox>
                            <Checkbox
                              isSelected={isLabelEnabled}
                              onValueChange={() =>
                                setIsLabelEnabled(!isLabelEnabled)
                              }
                            >
                              {t('Etiqueta')}
                            </Checkbox>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Action Buttons Card */}
          {isComparisonReady && (
            <Card className='p-4' ref={actionsCardRef}>
              <CardBody>
                <div className='flex flex-col md:flex-row justify-between items-center gap-4'>
                  <div className='flex-1'>
                    <h3 className='text-lg font-semibold text-default-700'>
                      {t('5. Acciones')}
                    </h3>
                    <p className='text-sm text-default-500'>
                      {t('Genera el comparativo y exporta los resultados')}
                    </p>
                  </div>
                  <div className='flex flex-col md:flex-row items-center w-full md:w-auto justify-center md:justify-end gap-3'>
                    {summaryStatistics && !isOptionsChanged && (
                      <>
                        <Button
                          variant='flat'
                          onClick={exportToPDF}
                          size='lg'
                          className='transition-all w-full md:w-auto'
                          startContent={
                            <Icon icon='mdi:file-pdf-box' className='text-xl' />
                          }
                        >
                          {t('Exportar PDF')}
                        </Button>
                        <Dropdown>
                          <DropdownTrigger>
                            <Button
                              variant='flat'
                              endContent={
                                <Icon
                                  icon='mdi:chevron-down'
                                  className='text-xl'
                                />
                              }
                              isDisabled={!isComparisonReady}
                              size='lg'
                              className='transition-all w-full md:w-auto'
                              startContent={
                                <Icon
                                  icon='mdi:microsoft-excel'
                                  className='text-xl'
                                />
                              }
                            >
                              {t('Exportar a Excel')}
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu aria-label='Export Options'>
                            <DropdownItem
                              key='resumen'
                              onClick={() => exportToExcel(false)}
                              description={t(
                                'Exporta un resumen por nave/semana y producto con formato simplificado, perfecto para compartir con clientes.'
                              )}
                            >
                              {t('Resumen')}
                            </DropdownItem>
                            <DropdownItem
                              key='resumen_detalle'
                              onClick={() => exportToExcel(true)}
                              description={t(
                                'Incluye una hoja detallada con el desglose completo de cada liquidación, mostrando el cálculo específico por producto y su impacto en el resultado final.'
                              )}
                            >
                              {t('Resumen + Detalle')}
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                        {summaryStatistics!.totalFobDifference < 0 && (
                          <>
                            <Button
                              variant='flat'
                              onClick={() => setShowClaimModal(true)}
                              size='lg'
                              className='transition-all w-full md:w-auto'
                              startContent={
                                <Icon
                                  icon='mdi:file-document-edit'
                                  className='text-xl'
                                />
                              }
                            >
                              {t('Guardar Comparativo')}
                            </Button>
                            {showClaimModal && (
                              <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                                <div className='bg-background rounded-lg p-6 w-full max-w-md'>
                                  <h3 className='text-lg font-semibold mb-4'>
                                    {t('Guardar Comparativo')}
                                  </h3>
                                  <div className='mb-4'>
                                    <label className='block text-sm font-medium text-default-600 mb-2'>
                                      {t('Descripción (opcional)')}
                                    </label>
                                    <textarea
                                      className='w-full p-2 border border-divider rounded-md'
                                      rows={3}
                                      placeholder={t(
                                        'Ingresa una descripción para este comparativo...'
                                      )}
                                      value={claimDescription}
                                      onChange={(e) =>
                                        setClaimDescription(e.target.value)
                                      }
                                    />
                                  </div>
                                  <div className='flex justify-end gap-3'>
                                    <Button
                                      variant='light'
                                      onClick={() => {
                                        setShowClaimModal(false);
                                        setClaimDescription('');
                                      }}
                                    >
                                      {t('Cancelar')}
                                    </Button>
                                    <Button
                                      color='primary'
                                      onClick={handleSaveClaim}
                                      isLoading={isSavingClaim}
                                    >
                                      {t('Guardar')}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                    <Button
                      color='primary'
                      onClick={handleGenerateComparison}
                      isDisabled={!isComparisonReady}
                      size='lg'
                      startContent={
                        <Icon icon='mdi:chart-box' className='text-xl' />
                      }
                    >
                      {t('Generar Comparativo')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          <Divider className='my-6' />

          {/* Results Section */}
          {summaryStatistics && comparisonClients.length > 0 && (
            <>
              {isOptionsChanged && (
                <Alert variant='warning' className='mb-4' showIcon>
                  {t(
                    'Has realizado cambios en las opciones. Por favor, presiona "Generar Comparativo" para actualizar los resultados.'
                  )}
                </Alert>
              )}
              <div ref={resultsRef} className='flex items-center gap-2'>
                <p className='text-2xl font-semibold'>
                  {t('Resultados del Análisis')}
                </p>
                <InformationTooltip>
                  <div className='p-2'>
                    <h3 className='font-semibold mb-2'>
                      {t('Resultados del Análisis:')}
                    </h3>
                    <ul className='list-disc pl-4'>
                      <li>
                        <strong>{t('Diferencia Total FOB:')}</strong>{' '}
                        {t(
                          'Muestra la diferencia total en FOB entre el cliente principal y los clientes de comparación. Se calcula sumando las diferencias de FOB de todas las liquidaciones seleccionadas.'
                        )}
                      </li>
                      <li>
                        <strong>{t('Diferencia FOB por Kilo:')}</strong>{' '}
                        {t(
                          'Muestra la diferencia en FOB por kilo entre el cliente principal y los clientes de comparación. Se calcula dividiendo la diferencia total de FOB por el total de kilos.'
                        )}
                      </li>
                      <li>
                        <strong>{t('Por Variedad:')}</strong>{' '}
                        {t(
                          'Tabla que muestra la comparación por variedad de productos. Los datos se agrupan y calculan según las variedades seleccionadas.'
                        )}
                      </li>
                      <li>
                        <strong>{t('Gráficos y Tablas:')}</strong>{' '}
                        {t(
                          'Incluye gráficos de barras y tablas para visualizar los datos de envío, comparación por variedad y calibre, y compensación de liquidaciones. Los gráficos se generan basándose en los datos filtrados y seleccionados.'
                        )}
                      </li>
                    </ul>
                  </div>
                </InformationTooltip>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 my-6 items-center'>
                <Card className='w-full min-h-52'>
                  <CardBody className='text-center flex flex-col justify-center items-center p-4'>
                    <Icon
                      icon='mdi:trending-up'
                      width={40}
                      height={40}
                      className='mb-4'
                    />
                    <p
                      className={`text-2xl font-semibold ${
                        summaryStatistics.totalFobDifference > 0
                          ? 'text-success-500'
                          : 'text-danger-500'
                      } mb-2`}
                    >
                      {formatMoney(summaryStatistics.totalFobDifference, {
                        decimals: 0,
                      })}
                    </p>
                    <p className='text-sm text-default-500'>
                      {comparisonType === 'byWeeks'
                        ? t(`Diferencia Total ${priceType} por Semanas`)
                        : comparisonType === 'byShips'
                          ? t(`Diferencia Total ${priceType} por Naves`)
                          : t(`Diferencia Total ${priceType} Personalizada`)}
                      {useLocalCurrency && effectiveLocalCurrency
                        ? ` (${effectiveLocalCurrency})`
                        : ''}
                    </p>
                    {summaryStatistics.comparablePercentage !== undefined && (
                      <p className='text-sm text-default-400 mt-1'>
                        ({summaryStatistics.comparablePercentage}%{' '}
                        {t('comparable')})
                      </p>
                    )}
                    {diffContext && (
                      <div className='mt-4 pt-3 border-t border-default-200 w-full'>
                        <div className='flex flex-wrap justify-center gap-3'>
                          {diffContext.pctOfFob !== null && (
                            <div
                              className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                summaryStatistics.totalFobDifference >= 0
                                  ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                  : 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                              }`}
                            >
                              {diffContext.pctOfFob > 0 ? '+' : ''}
                              {diffContext.pctOfFob.toFixed(1)}% {t('del')} FOB
                            </div>
                          )}
                          {diffContext.pctOfCommission !== null && (
                            <div
                              className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                summaryStatistics.totalFobDifference >= 0
                                  ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                  : 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                              }`}
                            >
                              {diffContext.pctOfCommission > 0 ? '+' : ''}
                              {diffContext.pctOfCommission.toFixed(1)}%{' '}
                              {t('de Comisión')}
                            </div>
                          )}
                        </div>
                        <div className='flex items-center justify-center gap-2 mt-2'>
                          <span
                            className={`text-[10px] ${!diffContextComparableOnly ? 'text-default-500 font-medium' : 'text-default-400'}`}
                          >
                            {t('Sobre Total Liquidaciones')}
                          </span>
                          <Switch
                            size='sm'
                            isSelected={diffContextComparableOnly}
                            onValueChange={setDiffContextComparableOnly}
                          />
                          <span
                            className={`text-[10px] ${diffContextComparableOnly ? 'text-default-500 font-medium' : 'text-default-400'}`}
                          >
                            {t('Sobre Sólo Comparable')}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
                <Card className='w-full min-h-52'>
                  <CardBody className='text-center flex flex-col justify-center items-center p-4'>
                    <Icon
                      icon='mdi:chart-line'
                      width={40}
                      height={40}
                      className='mb-4'
                    />
                    <p
                      className={`text-2xl font-semibold ${
                        summaryStatistics.perKiloDifference > 0
                          ? 'text-success-500'
                          : 'text-danger-500'
                      } mb-2`}
                    >
                      {formatMoney(summaryStatistics.perKiloDifference, {
                        decimals: 2,
                      })}
                    </p>
                    <p className='text-sm text-default-500'>
                      {comparisonType === 'byWeeks'
                        ? t(`Diferencia ${priceType} por Kilo por Semanas`)
                        : comparisonType === 'byShips'
                          ? t(`Diferencia ${priceType} por Kilo por Naves`)
                          : t(`Diferencia ${priceType} por Kilo Personalizada`)}
                      {useLocalCurrency && effectiveLocalCurrency
                        ? ` (${effectiveLocalCurrency})`
                        : ''}
                    </p>
                  </CardBody>
                </Card>
              </div>
            </>
          )}
          {shipmentData?.byWeeks &&
            shipmentData[
              comparisonType === 'custom' ? 'byShips' : comparisonType
            ] &&
            Object.keys(
              shipmentData[
                comparisonType === 'custom' ? 'byShips' : comparisonType
              ]
            ).length > 0 &&
            isComparisonReady && (
              <div className='space-y-6'>
                <div className='w-full'>
                  <RevenueBreakdownTable
                    data={shipmentData}
                    comparisonType={
                      comparisonType === 'custom' ? 'byShips' : comparisonType
                    }
                    priceType={priceType}
                    currencyLabel={currencyLabel}
                  />
                </div>
                <div className='w-full'>
                  <VarietyComparisionBarChart
                    data={shipmentData}
                    comparisonType={
                      comparisonType === 'custom' ? 'byShips' : comparisonType
                    }
                    currencyLabel={currencyLabel}
                  />
                </div>
                <div className='w-full'>
                  <ShipmentBarChart
                    data={shipmentData}
                    comparisonType={
                      comparisonType === 'custom' ? 'byShips' : comparisonType
                    }
                    currencyLabel={currencyLabel}
                  />
                </div>
                <div className='w-full'>
                  <CaliberComparisionBarChart
                    data={shipmentData}
                    comparisonType={
                      comparisonType === 'custom' ? 'byShips' : comparisonType
                    }
                    currencyLabel={currencyLabel}
                  />
                </div>
              </div>
            )}
        </div>
      </PageLayout>
    </div>
  );
};

export default SettlementsComparativeClients;
